import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import NumberField from '../../ui/NumberField';
import {
  Modal,
  Box,
  Typography,
  Skeleton,
  CircularProgress,
  Stack,
  Accordion,
  AccordionSummary,
  Alert,
  AlertTitle,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useInternalBooking from '../../../hooks/useInternalBooking';
import AuthContext from '../../../context/authContext';
import CallContext from '../../../context/callContext';
import usePayment from '../../../hooks/usePayment';
import useInvoice from '../../../hooks/useInvoice';
import useResource from '../../../hooks/useResource';
import { generateUniqueColor } from '../../../util/colorUtil';
import {
  hasNoShowInvoiceBillableLineItems,
  isNoShowActionable,
  isNoShowRevertInGracePeriod,
  isPaymentActionableByTiming,
  isPlainReductionBlocked,
  isTodayOrFuture,
  SPONTANEOUS_REDUCTION_POST_CHECKOUT_GRACE_MINUTES,
} from '../../../util/dateUtils';
import { isFeatureEnabled } from '../../../config/featureFlags';
import useNow from '../../../hooks/useNow';

// Component imports
import BookingHeader from './components/BookingHeader';
import WaitingForPaymentBanner from './components/WaitingForPaymentBanner';
import BookingInfoSection from './components/BookingInfoSection';
import BookingGroupSection from './components/BookingGroupSection';
import ResourceSection from './components/ResourceSection';
import BookingLineItemsTable from './components/BookingLineItemsTable';
import ActionButtons from './components/ActionButtons';
import BookingCommentSection from './components/BookingCommentSection';
import FooterButtons from './components/FooterButtons';
import MemberCardStatusSection from './components/MemberCardStatusSection';
import WaiverStatusSection from './components/WaiverStatusSection';
import OrderedResourceVisitPlan from './components/OrderedResourceVisitPlan';
import MarkNoShowButton from '../components/MarkNoShowButton';
import NoShowInvoiceButton, { hasNoShowInvoiceAdyenMerchant } from '../components/NoShowInvoiceButton';
import RevertNoShowButton from '../components/RevertNoShowButton';
import SkipNoShowInvoiceButton from '../components/SkipNoShowInvoiceButton';
import UnpaidBookingInvoiceButton, { hasOpenRetainedInsurance } from '../components/UnpaidBookingInvoiceButton';
import SkipUnpaidBookingInvoiceButton from '../components/SkipUnpaidBookingInvoiceButton';
import LockIconWrapper from '../../components/LockIconWrapper';

// Modal imports
import InvoiceModal from './components/InvoiceModal';
import ExternalInvoiceDialog from '../../Invoices/ExternalInvoiceDialog';
import PaymentHistoryModal from './components/PaymentHistoryModal';
import LineItemPaymentModal from './components/LineItemPaymentModal';
import CommunicationHistoryModal from './components/CommunicationHistoryModal';
import OnSiteProtocolModal from './components/OnSiteProtocolModal';
import BookingHistoryModal from './components/BookingHistoryModal';
import PackageSelectionDialog from './components/PackageSelectionDialog';
import PosTransferDialog from './components/PosTransferDialog';
import ShiftBookingDialog from './components/ShiftBookingDialog';
import LateArrivalDialog from './components/LateArrivalDialog';
import PaymentDialog from './components/PaymentDialog';
import CheckoutDialog from './components/CheckoutDialog';
import BadRatingConfirmDialog from './components/BadRatingConfirmDialog';
import UpsellSelectionDialog from './components/UpsellSelectionDialog';
import ConvertToOfflineDialog from './components/ConvertToOfflineDialog';
import SpontaneousReductionDialog from './components/SpontaneousReductionDialog';
import GoodwillCancellationDialog from './components/GoodwillCancellationDialog';
import MemberCardQueueModal from '../../MemberCards/MemberCardQueueModal';
import OnsiteQrModal from './components/OnsiteQrModal';
import useLasertagGame from '../../../hooks/useLasertagGame';
import usePendingBookings from '../../../hooks/usePendingBookings';

const normalizeOrderedResourceIdsForDisplay = booking => {
  if (!booking || !Array.isArray(booking.orderedResourceIds) || booking.orderedResourceIds.length === 0) {
    return booking;
  }

  const byGroup = new Map();
  booking.orderedResourceIds.forEach((segment, index) => {
    const groupRaw = Number(segment?.groupIndex);
    const groupIndex = Number.isFinite(groupRaw) && groupRaw > 0 ? groupRaw : 1;
    const orderRaw = Number(segment?.orderPosition);
    const orderPosition = Number.isFinite(orderRaw) && orderRaw > 0 ? orderRaw : index + 1;
    const offsetRaw = Number(segment?.startOffsetMinutes);
    const startOffsetMinutes = Number.isFinite(offsetRaw) ? offsetRaw : null;

    if (!byGroup.has(groupIndex)) {
      byGroup.set(groupIndex, []);
    }
    byGroup.get(groupIndex).push({
      segment,
      originalIndex: index,
      orderPosition,
      startOffsetMinutes,
    });
  });

  const normalizedSegments = [];
  const sortedGroupKeys = Array.from(byGroup.keys()).sort((a, b) => a - b);

  sortedGroupKeys.forEach(groupIndex => {
    const list = byGroup.get(groupIndex) || [];
    const hasAllOffsets = list.every(entry => Number.isFinite(entry.startOffsetMinutes));
    list.sort((a, b) => {
      if (hasAllOffsets) {
        if (a.startOffsetMinutes !== b.startOffsetMinutes) {
          return a.startOffsetMinutes - b.startOffsetMinutes;
        }
      }
      if (a.orderPosition !== b.orderPosition) {
        return a.orderPosition - b.orderPosition;
      }
      return a.originalIndex - b.originalIndex;
    });

    list.forEach((entry, idx) => {
      normalizedSegments.push({
        ...entry.segment,
        groupIndex,
        orderPosition: idx + 1,
      });
    });
  });

  return {
    ...booking,
    orderedResourceIds: normalizedSegments,
  };
};

const getOpenPaymentSummary = currentBooking => {
  const allPaymentRefs = (currentBooking?.lineItems || []).flatMap(item =>
    Array.isArray(item?.paymentReferences) ? item.paymentReferences : []
  );

  const unpaidRelatedOnlinePaymentIds = allPaymentRefs
    .filter(
      ref =>
        ref?.count > 0 &&
        !ref?.isPaid &&
        ref?.paymentType === 'online' &&
        ref?.relatedPaymentId
    )
    .map(ref => String(ref.relatedPaymentId));

  const hasUnpaidOnlinePayments = unpaidRelatedOnlinePaymentIds.length > 0;
  const hasUnpaidOfflinePayments = allPaymentRefs.some(
    ref => ref?.count > 0 && !ref?.isPaid && ref?.paymentType === 'offline'
  );

  return {
    hasUnpaidOnlinePayments,
    hasUnpaidOfflinePayments,
    unpaidRelatedOnlinePaymentIds: Array.from(new Set(unpaidRelatedOnlinePaymentIds)),
  };
};

function BookingDetailsModal({
  open,
  onClose,
  bookingId,
  onUpdate,
  borderColor = null,
  embedded = false,
  containerSx = null,
  bookingOverride = null,
  readOnly = false,
}) {
  const getErrorCode = error => {
    return (
      error?.code ||
      error?.response?.data?.code ||
      error?.response?.code ||
      error?.data?.code ||
      null
    );
  };

  const authCtx = useContext(AuthContext);
  const callCtx = useContext(CallContext);
  const {
    updateInternalBooking,
    getAvailableCapacity,
    cancelBooking,
    cancelBlocker,
    cancelBookingParticipants,
    spontaneousReduction,
    revokeSpontaneousReduction,
    previewSpontaneousReduction,
    previewGoodwillCancellation,
    cancelBookingWithGoodwillVoucher,
    addBookingParticipants,
    updateBookingPackage,
    getAvailablePackages,
    getAvailableShiftDates,
    getAvailableShiftTimes,
    shiftBooking,
    markCustomerAsPresent,
    rewindCheckin,
    rewindCheckout,
    markBookingAsFullyPaid,
    markLineItemsAsPaid,
    markBookingAsFinished,
    getInternalBookingById,
    getPotentialDuplicateInternalBookings,
    getAvailableUpsells,
    addUpsell,
    updateUpsells,
    updateSpecialLineItems,
    updateBookingField,
    resendBookingConfirmation,
    sendPaymentLink,
    removeVoucherFromBooking,
    getVoucherMergeCandidates,
    mergeVoucherBookings,
    addBookingCategory,
    overrideLineItemPrice,
    addSpecialLineItem,
  } = useInternalBooking();
  const { convertToOfflinePaymentSilent, cancelPaymentsSilent } = usePayment();
  const { createExternalInvoice, getInvoicePDFById, getBookingInvoices } = useInvoice();
  const { getResourceById } = useResource();
  const getResourceByIdRef = useRef(getResourceById);
  const prevFinishedAtRef = useRef(null);

  useEffect(() => {
    getResourceByIdRef.current = getResourceById;
  }, [getResourceById]);

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  // Sibling navigation inside a BookingGroup ("Warenkorb"): when set (and belonging
  // to the currently opened root booking), the modal shows the sibling booking
  // instead of the booking it was opened with.
  const [siblingView, setSiblingView] = useState(null);
  const effectiveBookingId =
    siblingView && String(siblingView.base) === String(bookingId) ? siblingView.id : bookingId;
  const [comment, setComment] = useState('');
  const [customerComment, setCustomerComment] = useState('');
  const [originalComment, setOriginalComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [lineItemCount, setLineItemCount] = useState(null);
  const [oldLineItemCount, setOldLineItemCount] = useState(null);
  const [cancelCountdown, setCancelCountdown] = useState(0);
  const [countdownStart, setCountdownStart] = useState(null);
  const [lockCancelBtn, setLockCancelBtn] = useState(false);
  const [presentCountdown, setPresentCountdown] = useState(0);
  const [presentCountdownStart, setPresentCountdownStart] = useState(null);
  const [availableCapacity, setAvailableCapacity] = useState(0);
  const [minParticipants, setMinParticipants] = useState(null);
  const [maxParticipants, setMaxParticipants] = useState(null);
  const [overbookEnabled, setOverbookEnabled] = useState(false);
  const [canOverbook, setCanOverbook] = useState(false);
  const [shiftOverbookEnabled, setShiftOverbookEnabled] = useState(false);
  const [packageSelectionOpen, setPackageSelectionOpen] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timesLoadedForDate, setTimesLoadedForDate] = useState(null);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs().month() + 1);
  const [currentYear, setCurrentYear] = useState(dayjs().year());
  const [communicationHistoryOpen, setCommunicationHistoryOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [posTransferOpen, setPosTransferOpen] = useState(false);
  const [externalInvoiceDialogOpen, setExternalInvoiceDialogOpen] = useState(false);
  const [externalInvoice, setExternalInvoice] = useState(null);
  const [extPdfViewerOpen, setExtPdfViewerOpen] = useState(false);
  const [extPdfUrl, setExtPdfUrl] = useState(null);
  const [extPaymentWarningOpen, setExtPaymentWarningOpen] = useState(false);
  const [openExtInvoiceAfterPayment, setOpenExtInvoiceAfterPayment] = useState(false);
  const [pendingExternalInvoicePayload, setPendingExternalInvoicePayload] = useState(null);
  const [lateArrivalModalOpen, setLateArrivalModalOpen] = useState(false);
  const [rewindCheckinConfirmOpen, setRewindCheckinConfirmOpen] = useState(false);
  const [rewindCheckinLoading, setRewindCheckinLoading] = useState(false);
  const [rewindCheckoutConfirmOpen, setRewindCheckoutConfirmOpen] = useState(false);
  const [rewindCheckoutLoading, setRewindCheckoutLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentCountdown, setPaymentCountdown] = useState(0);
  const [paymentCountdownStart, setPaymentCountdownStart] = useState(null);
  const [lockPaymentBtn, setLockPaymentBtn] = useState(false);
  const [onSiteProtocolModalOpen, setOnSiteProtocolModalOpen] = useState(false);
  const [bookingHistoryModalOpen, setBookingHistoryModalOpen] = useState(false);
  const [paymentHistoryModalOpen, setPaymentHistoryModalOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [badRatingConfirmOpen, setBadRatingConfirmOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutCountdown, setCheckoutCountdown] = useState(0);
  const [addSpecialItemOpen, setAddSpecialItemOpen] = useState(false);
  const [checkoutCountdownStart, setCheckoutCountdownStart] = useState(null);
  const [lockCheckoutBtn, setLockCheckoutBtn] = useState(false);
  const [prepTimeMinutes, setPrepTimeMinutes] = useState(0);
  const [prepTimeWarnings, setPrepTimeWarnings] = useState([]);
  const [selectedLineItem, setSelectedLineItem] = useState(null);

  // If a booking was checked out and checkout gets reverted, ensure UI unlocks immediately.
  useEffect(() => {
    const prevHadCheckout = Boolean(prevFinishedAtRef.current);
    const nowHasCheckout = Boolean(booking?.finishedAt);
    if (prevHadCheckout && !nowHasCheckout) {
      setLockCheckoutBtn(false);
      setCheckoutLoading(false);
    }
    prevFinishedAtRef.current = booking?.finishedAt || null;
  }, [booking?.finishedAt]);
  const [lineItemPaymentModalOpen, setLineItemPaymentModalOpen] = useState(false);
  const [convertingPayment, setConvertingPayment] = useState(false);
  const [convertToOfflineConfirmOpen, setConvertToOfflineConfirmOpen] = useState(false);
  const [cancelPaymentConfirmOpen, setCancelPaymentConfirmOpen] = useState(false);
  const [spontaneousReductionOpen, setSpontaneousReductionOpen] = useState(false);
  const [goodwillCancellationOpen, setGoodwillCancellationOpen] = useState(false);
  const [goodwillCanceling, setGoodwillCanceling] = useState(false);
  const [spontaneousReducing, setSpontaneousReducing] = useState(false);
  // Reduction entry whose MOP-show voucher(s) staff wants to revoke (confirm dialog target).
  const [revokeReductionTarget, setRevokeReductionTarget] = useState(null);
  const [revokingReduction, setRevokingReduction] = useState(false);
  // When a plain reduction is gated (<7 days, no insurance) and the user may override
  // (unlimitedEdits), this holds { categoryId, countToCancel } to drive the decision dialog.
  const [reductionGateDialog, setReductionGateDialog] = useState(null);
  // Count prefilled into the spontaneous-reduction dialog when it is opened from the gate dialog.
  const [spontaneousReductionInitialCount, setSpontaneousReductionInitialCount] = useState(null);
  const [loadingDuplicateBookings, setLoadingDuplicateBookings] = useState(false);
  const [duplicateBookingCheckFailed, setDuplicateBookingCheckFailed] = useState(false);
  const [potentialDuplicateBookings, setPotentialDuplicateBookings] = useState([]);
  const [voucherMergeCandidates, setVoucherMergeCandidates] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [upsellSelectionOpen, setUpsellSelectionOpen] = useState(false);
  const [availableUpsells, setAvailableUpsells] = useState([]);
  const [editingUpsell, setEditingUpsell] = useState(null);
  const [upsellCount, setUpsellCount] = useState(null);
  const [upsellType, setUpsellType] = useState(null);
  const [editingSpecialLineItem, setEditingSpecialLineItem] = useState(null);
  const [specialLineItemCount, setSpecialLineItemCount] = useState(null);
  const [currentlyEditingField, setCurrentlyEditingField] = useState(null);
  const [memberCardModalOpen, setMemberCardModalOpen] = useState(false);
  const [capacityOverrideDialogOpen, setCapacityOverrideDialogOpen] = useState(false);
  const [capacityOverrideEditParticipant, setCapacityOverrideEditParticipant] = useState('');
  const [capacityOverrideEditLane, setCapacityOverrideEditLane] = useState('');
  const [capacityOverrideSaving, setCapacityOverrideSaving] = useState(false);
  const [onsiteQrOpen, setOnsiteQrOpen] = useState(false);
  const [memberDetailsMap, setMemberDetailsMap] = useState(null);
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [sendPaymentLinkOpen, setSendPaymentLinkOpen] = useState(false);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [changingGroupType, setChangingGroupType] = useState(false);
  const [groupTypeConfirmOpen, setGroupTypeConfirmOpen] = useState(false);
  const [groupTypeCapacityInfo, setGroupTypeCapacityInfo] = useState(null);
  const [loadingGroupTypeCapacity, setLoadingGroupTypeCapacity] = useState(false);
  const [groupTypeFlipDialog, setGroupTypeFlipDialog] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryDialogCategories, setCategoryDialogCategories] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [selectedCategoryCount, setSelectedCategoryCount] = useState(1);
  const [categoryDialogAgeSplit, setCategoryDialogAgeSplit] = useState(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [categoryDialogOverbookEnabled, setCategoryDialogOverbookEnabled] = useState(false);

  const isPreview = Boolean(readOnly || (bookingOverride && typeof bookingOverride === 'object'));
  const regiondoBookingKey = booking?.legacyMeta?.regiondo?.booking_key
    ? String(booking.legacyMeta.regiondo.booking_key).trim()
    : '';
  const isRegiondoSourcedBooking = Boolean(regiondoBookingKey);
  const regiondoPaymentStatus = booking?.legacyMeta?.regiondo?.payment_status
    ? String(booking.legacyMeta.regiondo.payment_status).trim().toLowerCase()
    : '';
  const isRegiondoPaymentOnlinePaid = (() => {
    if (!regiondoPaymentStatus) return false;
    if (regiondoPaymentStatus === 'paid_online' || regiondoPaymentStatus === 'paid_voucher')
      return true;
    if (!regiondoPaymentStatus.startsWith('paid')) return false;
    if (
      regiondoPaymentStatus.includes('offline') ||
      regiondoPaymentStatus.includes('cash') ||
      regiondoPaymentStatus.includes('onsite') ||
      regiondoPaymentStatus.includes('on_site') ||
      regiondoPaymentStatus.includes('bar')
    )
      return false;
    return true;
  })();
  const isRegiondoOnlinePaidBooking = Boolean(
    isRegiondoSourcedBooking &&
    (booking?.initialPayment === 'online' || isRegiondoPaymentOnlinePaid)
  );
  const isRegiondoOfflinePaidBooking = Boolean(
    isRegiondoSourcedBooking && !isRegiondoOnlinePaidBooking
  );
  const bookingForOrderedResourcesDisplay = useMemo(
    () => normalizeOrderedResourceIdsForDisplay(booking),
    [booking]
  );

  const { getMembersByIds } = useLasertagGame();
  const { confirmPendingBooking, rejectPendingBooking } = usePendingBookings();
  const isDev = Boolean(import.meta.env.DEV || import.meta.env.VITE_IS_DEV);

  // keep latest booking and fetchers in refs to avoid effect loops
  const bookingRef = useRef(booking);
  useEffect(() => {
    bookingRef.current = booking;
  }, [booking]);
  const getMembersByIdsRef = useRef(getMembersByIds);
  useEffect(() => {
    getMembersByIdsRef.current = getMembersByIds;
  }, [getMembersByIds]);

  const addableCategories = useMemo(
    () => (Array.isArray(booking?.addableCategories) ? booking.addableCategories : []),
    [booking?.addableCategories]
  );
  const allCategoriesUsed = addableCategories.length === 0;

  const bookingDate = booking ? dayjs(booking.startTime) : null;
  const isTodayorFutue = bookingDate ? isTodayOrFuture(bookingDate) : false;
  const isToday = bookingDate ? bookingDate.isSame(dayjs(), 'day') : false;
  const nowTs = useNow();
  const openPaymentSummary = useMemo(() => getOpenPaymentSummary(booking), [booking]);
  // Staff may resolve an open/failed online payment immediately (e.g. the customer is on-site
  // after a declined card): convert it to offline and then mark it paid, or cancel it. The
  // backend already permits this at any time, so the banner actions are no longer gated by the
  // buildStaleWaitingForPaymentFilter timing. During the early grace window — where the online
  // payment might still complete on its own — the actions stay available but ask for an extra
  // confirmation first (see ConvertToOfflineDialog / the cancel confirm dialog below).
  const hasUnpaidOnlinePayments = openPaymentSummary.hasUnpaidOnlinePayments;
  const isPaymentWithinGraceWindow =
    hasUnpaidOnlinePayments && !isPaymentActionableByTiming(booking, nowTs);
  const isWithinNext70Hours = useMemo(() => {
    if (!bookingDate) return false;
    const now = dayjs();
    return bookingDate.isAfter(now) && bookingDate.diff(now, 'hour') <= 70;
  }, [bookingDate]);
  // Holders of the late-reduction right keep the spontaneous-reduction window open up to one
  // month after checkout (instead of the 10-minute grace) — mirrors the backend
  // internalBooking:special:spontaneousReductionLate handling.
  const hasLateReductionRights = useMemo(
    () =>
      open ? authCtx.checkRightAny('internalBooking:special:spontaneousReductionLate') : false,
    [open, authCtx]
  );
  // Spontaneous reduction (full price + MOP-show voucher) is available for staff at any time
  // before the start — mirroring the backend endpoint (isWithinStaffReductionWindow with
  // openBeforeStart). After the start the button stays available through the visit: during the
  // small post-start grace, while the group is checked in and not yet checked out, and for
  // 2 days after checkout (one month with the late-reduction right). nowTs re-evaluates
  // over time so the button drops off live.
  const isWithinReductionWindow = useMemo(() => {
    if (!bookingDate) return false;
    const now = dayjs();
    // The entire pre-start period is open (the 7-day opening only applies to the free-reduction
    // redirect gate, not to the staff voucher flow itself).
    if (bookingDate.isAfter(now)) return true;
    const hoursUntil = bookingDate.diff(now, 'hour');
    if (hoursUntil > -3) return true;
    if (booking?.presentSince && !booking?.finishedAt) return true;
    if (booking?.finishedAt) {
      if (hasLateReductionRights) {
        return !now.isAfter(dayjs(booking.finishedAt).add(1, 'month'));
      }
      return (
        now.diff(dayjs(booking.finishedAt), 'minute', true) <=
        SPONTANEOUS_REDUCTION_POST_CHECKOUT_GRACE_MINUTES
      );
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDate, booking?.presentSince, booking?.finishedAt, hasLateReductionRights, nowTs]);
  const bookingHasInsuranceTag = useMemo(() => {
    const tags = booking?.tags || [];
    const pkgTags = booking?.selectedPackage?.tags || [];
    const upsellTags = (booking?.upsells || []).flatMap(u => u?.tags || []);
    return [...tags, ...pkgTags, ...upsellTags].includes('hasInsurance');
  }, [booking]);
  // Highest minimum participant count demanded by the booked package/upsells (0 when none). A
  // regular refunding cancellation can never undershoot it and no staff right lifts it, so below
  // this threshold the goodwill voucher is the only way to compensate absent people.
  const bookedUpgradeMinimumCount = useMemo(() => {
    const bookedUpgrades = [booking?.selectedPackage, ...(booking?.upsells || [])];
    return bookedUpgrades.reduce((max, bookedUpgrade) => {
      const minimumCount = Number(bookedUpgrade?.minimumCount);
      return Number.isFinite(minimumCount) && minimumCount > max ? minimumCount : max;
    }, 0);
  }, [booking]);
  // Ticket insurance entitles the customer to a real refunding cancellation, so the voucher flow
  // stays hidden for insured bookings — unless a booked upgrade minimum blocks that cancellation.
  // With a minimum of 2 or more there is at least one reduction (down to 1 participant) the refund
  // cannot cover, so the button is offered; the dialog then restricts it to those counts.
  const insuranceAllowsReductionByUpgradeMinimum =
    bookingHasInsuranceTag && bookedUpgradeMinimumCount > 1;
  // People still present, not people booked: an earlier spontaneous reduction leaves
  // participantCount untouched (the full price stays due), so a booking whose last-but-one guest
  // has already been reduced away has nothing left to reduce.
  const presentParticipantCount = Math.max(
    0,
    (Number(booking?.participantCount) || 0) -
      (booking?.spontaneousReductions || [])
        .filter(rd => rd && rd.status !== 'canceled')
        .reduce((sum, rd) => sum + (Number(rd.count) || 0), 0)
  );
  // A reduction is possible regardless of payment state, including bookings whose on-site cash was
  // already collected: the booked seats are never refunded (full price stays due), only the goodwill
  // voucher is issued (immediately when fully paid, otherwise deferred). So payment status does not
  // gate the button — mirrors the backend, which no longer rejects an already-paid booking.
  const canSpontaneousReduce =
    booking?.status === 'confirmed' &&
    isWithinReductionWindow &&
    (!bookingHasInsuranceTag || insuranceAllowsReductionByUpgradeMinimum) &&
    presentParticipantCount > 1;

  // Goodwill cancellation ("Storno mit Kulanz-Gutschein"): leadership-only cancel of an
  // already-paid booking without refund — the paid amount minus the ticket-insurance share
  // is issued as a goodwill voucher instead. Insured bookings keep their entitlement to a
  // refunding cancellation, so the button stays hidden for them (backend enforces the same).
  const hasGoodwillCancelRight = useMemo(
    () =>
      open ? authCtx.checkRightAny('internalBooking:special:cancelWithGoodwillVoucher') : false,
    [open, authCtx]
  );
  const hasPaidMoneyReference = useMemo(
    () =>
      (booking?.lineItems || []).some(
        li =>
          li.status === 'active' &&
          (li.paymentReferences || []).some(
            ref =>
              !!ref.isPaid &&
              (Number(ref.count) || 0) > 0 &&
              ['online', 'onlineLink', 'offline'].includes(String(ref.paymentType || ''))
          )
      ),
    [booking]
  );
  const canGoodwillCancel =
    hasGoodwillCancelRight &&
    booking?.status === 'confirmed' &&
    !bookingHasInsuranceTag &&
    hasPaidMoneyReference;

  const handleOpenRegiondo = async () => {
    if (!booking || !regiondoBookingKey) return;
    window.open(
      `https://www.regiondo.de/resellers/account/bookingsview/key/${regiondoBookingKey}`,
      '_blank'
    );
  };

  const memberCardStats = useMemo(() => {
    if (!booking) return { namesToAsk: 0, cardsToPrint: 0, cardsPrinting: 0, cardsToLink: 0 };
    const raw = booking?.ltSpecific?.participatingMembers || [];
    const members = raw.map(m => {
      if (typeof m === 'string') {
        const d = memberDetailsMap ? memberDetailsMap.get(String(m)) : null;
        return d ? { _id: String(m), ...d } : { _id: String(m) };
      }
      if (m && typeof m === 'object' && typeof m._id === 'string') {
        if (m.playerName === undefined || m.cardStatus === undefined) {
          const d = memberDetailsMap ? memberDetailsMap.get(String(m._id)) : null;
          return d ? { ...m, ...d } : m;
        }
        return m;
      }
      return m;
    });
    const hasAnyCard = mm => {
      const ids = Array.isArray(mm?.cardIDs) ? mm.cardIDs : mm?.cardID ? [mm.cardID] : [];
      return ids.length > 0;
    };
    const isPlaceholder = mm => {
      try {
        if (!mm) return true;
        if (typeof mm === 'string') return false;
        const hasFirst = !!(mm.firstName && String(mm.firstName).trim().length > 0);
        const hasPlayer = !!(mm.playerName && String(mm.playerName).trim().length > 0);
        return !(hasFirst || hasPlayer);
      } catch (_) {
        return false;
      }
    };
    const participantCount = booking?.participantCount || 0;
    const nonPlaceholderMembers = members.filter(m => !isPlaceholder(m));
    const placeholdersCount = members.length - nonPlaceholderMembers.length;
    const effectiveBooked = Math.max(0, participantCount - placeholdersCount);
    const missingSlots = Math.max(effectiveBooked - nonPlaceholderMembers.length, 0);
    const namesMissingForExisting = nonPlaceholderMembers.filter(m => {
      const hasName = !!(m && m.playerName && String(m.playerName).trim().length > 0);
      const isPrinted = (m && m.cardStatus) === 'printed';
      return !isPrinted && !hasName;
    }).length;
    const namesToAsk = missingSlots + namesMissingForExisting;
    const cardsToPrint = nonPlaceholderMembers.filter(m => {
      const status = m && m.cardStatus ? m.cardStatus : 'open';
      if (
        status === 'printed' ||
        status === 'confirmed' ||
        status === 'printing' ||
        status === 'reprint_requested'
      )
        return false;
      return !hasAnyCard(m);
    }).length;
    const cardsPrinting = nonPlaceholderMembers.filter(
      m => (m && m.cardStatus) === 'confirmed'
    ).length;
    const cardsToLink = nonPlaceholderMembers.filter(
      m => (m && m.cardStatus) === 'link_failed'
    ).length;
    return { namesToAsk, cardsToPrint, cardsPrinting, cardsToLink };
  }, [booking, memberDetailsMap]);

  const memberStatsReady = useMemo(() => {
    if (!booking) return false;
    const pm = booking?.ltSpecific?.participatingMembers || [];
    const needsEnrichment = pm.some(
      m =>
        typeof m === 'string' ||
        (m &&
          typeof m === 'object' &&
          typeof m._id === 'string' &&
          (m.playerName === undefined || m.cardStatus === undefined))
    );
    return !needsEnrichment || !!memberDetailsMap;
  }, [booking, memberDetailsMap]);

  // Lock editing when pending online payment for today or future
  const hasPendingOnlinePaymentLock = useMemo(() => {
    if (!bookingDate) return false;
    const isFutureOrToday = isTodayOrFuture(bookingDate);
    return openPaymentSummary.hasUnpaidOnlinePayments && isFutureOrToday;
  }, [bookingDate, openPaymentSummary.hasUnpaidOnlinePayments]);

  const updateEditableFromBooking = (nextBooking, allowUnlimitedEdits = false) => {
    if (!nextBooking) return;
    const isFutureOrToday = isTodayOrFuture(nextBooking.startTime);
    const hasPendingOnlinePayment =
      getOpenPaymentSummary(nextBooking).hasUnpaidOnlinePayments && isFutureOrToday;
    const isEditableNow = nextBooking.status === 'noShow'
      ? false
      : !hasPendingOnlinePayment
        ? allowUnlimitedEdits
          ? true
          : nextBooking.status !== 'canceled' && !nextBooking.finishedAt
        : false;
    setIsEditable(isEditableNow);
  };

  // Only calculate rights when modal is open
  const hasMinorUpdateRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:update:minor') : false),
    [open, authCtx]
  );
  const hasUpdateRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:update:own') : false),
    [open, authCtx]
  );
  const hasFullUpdateRights = useMemo(
    () => (open ? authCtx.checkRight('internalBooking:special:updateCustomerData') : false),
    [open, authCtx]
  );
  const hasCustomerEmailUpdateRights = useMemo(
    () => (open ? authCtx.checkRight('internalBooking:special:updateCustomerEmail') : false),
    [open, authCtx]
  );

  const hasUnlimitedEditRights = useMemo(
    () => (open ? authCtx.checkRight('internalBooking:special:unlimitedEdits') : false),
    [open, authCtx]
  );

  const hasRewindCheckinRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:rewindCheckin') : false),
    [open, authCtx]
  );

  const hasRegiondoOnlinePaidEditRights = useMemo(
    () => (open ? authCtx.checkRight('internalBooking:special:regiondoOnlinePaidEdit') : false),
    [open, authCtx]
  );

  const canSeeAddress = useMemo(
    () => (open ? authCtx.checkRight('internalBooking:special:seeAddress') : false),
    [open, authCtx]
  );

  const hasPosTransferRight = useMemo(
    () =>
      open && isFeatureEnabled('posTransfer')
        ? authCtx.checkRight('internalBooking:special:posTransfer')
        : false,
    [open, authCtx]
  );

const hasPastCancelRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:cancelPast') : false),
    [open, authCtx]
  );
  const hasLastMinuteCancelRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:cancelLastMinute') : false),
    [open, authCtx]
  );
  // Narrow alternative to unlimitedEdits: reduce participants with a real refund inside the
  // 7-day window without ticket insurance (backend canOverride of the reduction gate).
  const hasForceReduceLastMinuteRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:forceReduceLastMinute') : false),
    [open, authCtx]
  );
  const hasConfirmRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:confirmpending') : false),
    [open, authCtx]
  );

  const canShowCallButton = useMemo(
    () => (open ? authCtx.checkRightAny('call:runcall') : false),
    [open, authCtx]
  );
  const hasOverrideCapacityRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:overrideCapacity') : false),
    [open, authCtx]
  );
  const hasOverbookRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:overbook') : false),
    [open, authCtx]
  );
  const hasChangeGroupTypeRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:update') : false),
    [open, authCtx]
  );
  const hasAddCategoryRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:update') : false),
    [open, authCtx]
  );
  const hasSuppressEmailsRight = useMemo(
    () => (open ? authCtx.checkRight('internalBooking:special:suppressEmails') : false),
    [open, authCtx]
  );
  const hasOverridePricesRight = useMemo(
    () => (open ? authCtx.checkRight('internalBooking:special:overridePrices') : false),
    [open, authCtx]
  );
  const hasActiveCallAgent = Boolean(callCtx?.selectedAgent);

  const hasMarkNoShowRight = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:markNoShow') : false),
    [open, authCtx]
  );

  const hasSendNoShowInvoiceRight = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:sendNoShowInvoice') : false),
    [open, authCtx]
  );

  const hasSendUnpaidBookingInvoiceRight = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:sendUnpaidBookingInvoice') : false),
    [open, authCtx]
  );

  const hasRevertNoShowRight = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:revertNoShow') : false),
    [open, authCtx]
  );

  const hasSendPaymentLinkRights = useMemo(
    () => (open ? authCtx.checkRightAny('internalBooking:special:sendPaymentLink') : false),
    [open, authCtx]
  );

  // Staff action "Bezahllink senden" (backend sendPaymentLink endpoint): with unpaid online
  // payments the existing link is re-sent (fresh payment if the old one is dead); otherwise
  // the unpaid on-site payment is switched to online first — mirroring the customer
  // self-service switch eligibility (status confirmed, ≥6h lead, no value vouchers; the
  // backend enforces the same rules).
  const sendPaymentLinkMode = hasUnpaidOnlinePayments ? 'resend' : 'switch';
  const hasAppliedValueVouchers =
    Array.isArray(booking?.appliedVouchers) && booking.appliedVouchers.length > 0;
  const hoursUntilStart = booking?.startTime ? dayjs(booking.startTime).diff(dayjs(nowTs), 'hour') : -1;
  const canSendPaymentLinkOffline =
    Boolean(booking) &&
    hasSendPaymentLinkRights &&
    !isRegiondoOnlinePaidBooking &&
    booking?.status === 'confirmed' &&
    booking?.fullyPaid !== true &&
    Boolean(booking?.mail) &&
    openPaymentSummary.hasUnpaidOfflinePayments &&
    !hasUnpaidOnlinePayments &&
    !hasAppliedValueVouchers &&
    // ONLINE_PAYMENT_SWITCH_MIN_HOURS in the backend
    hoursUntilStart >= 6;

  const canRenderNoShowActionsRow = useMemo(() => {
    if (!open || isPreview) return false;
    if (!booking) return false;
    if (booking.status === 'noShow') {
      const canRenderRevertAction =
        !booking.noShowInvoiceId &&
        !booking.noShowInvoiceSkippedAt &&
        !booking.noShowInvoicePaidAt &&
        (isNoShowRevertInGracePeriod(booking, nowTs) || hasRevertNoShowRight);
      const canRenderInvoiceActions =
        hasSendNoShowInvoiceRight &&
        hasNoShowInvoiceAdyenMerchant(booking) &&
        !booking.noShowInvoiceSkippedAt &&
        (booking.noShowInvoiceId || hasNoShowInvoiceBillableLineItems(booking));
      return canRenderRevertAction || canRenderInvoiceActions;
    }

    if (!hasMarkNoShowRight && !hasSendNoShowInvoiceRight && !hasRevertNoShowRight) {
      return false;
    }

    if (booking.presentSince) return false;

    if (hasMarkNoShowRight && isNoShowActionable(booking)) return true;

    return false;
  }, [
    open,
    isPreview,
    booking,
    hasMarkNoShowRight,
    hasSendNoShowInvoiceRight,
    hasRevertNoShowRight,
    nowTs,
  ]);

  // Visibility of the "unpaid booking" invoice action: only for a CANCELLED booking that
  // still carries an open, non-cancellable ticket insurance ("Ticketversicherung").
  const canShowUnpaidBookingInvoiceRow = useMemo(() => {
    if (!open || isPreview) return false;
    if (!hasSendUnpaidBookingInvoiceRight) return false;
    return hasOpenRetainedInsurance(booking);
  }, [open, isPreview, booking, hasSendUnpaidBookingInvoiceRight]);

  const effectiveHasMinorUpdateRights = !isPreview && hasMinorUpdateRights;
  const effectiveHasUpdateRights = !isPreview && hasUpdateRights;
  const effectiveHasFullUpdateRights = !isPreview && hasFullUpdateRights;
  const effectiveHasCustomerEmailUpdateRights = !isPreview && hasCustomerEmailUpdateRights;
  const effectiveHasUnlimitedEditRights = !isPreview && hasUnlimitedEditRights;
  const effectiveHasRewindCheckinRights = !isPreview && hasRewindCheckinRights;
  const effectiveHasRegiondoOnlinePaidEditRights = !isPreview && hasRegiondoOnlinePaidEditRights;
  const effectiveHasPastCancelRights = !isPreview && hasPastCancelRights;
  const effectiveHasLastMinuteCancelRights = !isPreview && hasLastMinuteCancelRights;
  const effectiveHasConfirmRights = !isPreview && hasConfirmRights;

  const canCancelRegiondoOnlinePaidBooking = Boolean(
    isRegiondoOnlinePaidBooking && effectiveHasRegiondoOnlinePaidEditRights
  );

  const effectiveHasMinorUpdateRightsUi =
    !isRegiondoOnlinePaidBooking && effectiveHasMinorUpdateRights;
  const effectiveHasUpdateRightsUi = !isRegiondoOnlinePaidBooking && effectiveHasUpdateRights;
  const effectiveHasFullUpdateRightsUi =
    !isRegiondoOnlinePaidBooking && effectiveHasFullUpdateRights;
  const effectiveHasCustomerEmailUpdateRightsUi =
    !isRegiondoOnlinePaidBooking && effectiveHasCustomerEmailUpdateRights;
  const effectiveHasUnlimitedEditRightsUi =
    !isRegiondoOnlinePaidBooking && effectiveHasUnlimitedEditRights;
  const effectiveHasPastCancelRightsUi =
    !isRegiondoOnlinePaidBooking && effectiveHasPastCancelRights;
  const effectiveHasLastMinuteCancelRightsUi =
    !isRegiondoOnlinePaidBooking && effectiveHasLastMinuteCancelRights;
  const effectiveHasConfirmRightsUi = !isRegiondoOnlinePaidBooking && effectiveHasConfirmRights;

  const isEditableEffective = Boolean(isEditable && !isRegiondoOnlinePaidBooking);
  const isWorkflowEditableEffective = Boolean(isEditable);

  // Within the free-reduction window (from 7 days before the start through the visit) and without
  // ticket insurance the backend rejects a plain refunding reduction and demands the
  // spontaneous-reduction (MOP-show voucher) flow — only unlimitedEdits/forceReduceLastMinute may
  // override it. The count pencil must not offer a reduction that cannot go through; it stays
  // available for *adding* participants (see BookingLineItemsTable).
  const canForceReduce = effectiveHasUnlimitedEditRightsUi || hasForceReduceLastMinuteRights;
  const plainReductionBlocked = useMemo(
    () =>
      isPlainReductionBlocked(
        booking,
        { canForceReduce, latePostCheckout: hasLateReductionRights },
        nowTs
      ),
    [booking, canForceReduce, hasLateReductionRights, nowTs]
  );

  const computedBorderColor = useMemo(() => {
    const pgId = booking?.ltSpecific?.playerGroupID;
    if (pgId === 0) return 'orange';
    if (pgId === 1) return 'green';
    if (borderColor === null) return null;
    if (typeof borderColor === 'string' && borderColor) return borderColor;
    if (booking?.resourceId) {
      const resourceId = booking.resourceId._id || booking.resourceId;
      return generateUniqueColor(resourceId.toString());
    }
    return null;
  }, [borderColor, booking?.resourceId?._id, booking?.entertainment, booking?.ltSpecific?.playerGroupID]);

  const addressLines = useMemo(() => {
    if (!booking || !booking.contactData) return [];
    const { street, postcode, city } = booking.contactData || {};
    const lines = [];

    const line1 = [street].filter(Boolean).join(' ').trim();
    const line2 = [postcode != null ? String(postcode) : null, city]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (line1) lines.push(line1);
    if (line2) lines.push(line2);

    return lines.filter(Boolean);
  }, [booking]);

  const hasAddress = addressLines.length > 0;

  const hasBirthdate = useMemo(
    () => Boolean(booking?.contactData?.birthdate),
    [booking?.contactData?.birthdate]
  );

  const birthdateDisplay = useMemo(() => {
    if (!booking?.contactData?.birthdate) return null;
    return dayjs(booking.contactData.birthdate).format('DD.MM.YYYY');
  }, [booking?.contactData?.birthdate]);

  useEffect(() => {
    const shouldLoadBooking = embedded || open;
    if (!shouldLoadBooking) return;

    if (bookingOverride && typeof bookingOverride === 'object') {
      const b = bookingOverride;
      setLoading(false);
      setBooking(b);
      setComment(b.comment || '');
      setOriginalComment(b.comment || '');
      setCustomerComment(b.customerComment || '');
      setPrepTimeMinutes(b.entertainmentId?.prepTimeMinutes || 0);
      setPrepTimeWarnings(b.entertainmentId?.prepTimeWarnings || []);
      setIsEditable(false);

      let canceled = false;
      const hydrateResource = async () => {
        const rid = b?.resourceId?._id || b?.resourceId || null;
        if (!rid) return;
        if (b?.resourceId && typeof b.resourceId === 'object' && b.resourceId.name) return;
        try {
          const res = await getResourceByIdRef.current(String(rid));
          const resource = res?.resource || res;
          const normalized =
            resource && typeof resource === 'object'
              ? { ...resource, _id: resource?._id ?? resource?.id }
              : null;
          if (canceled || !normalized?._id) return;
          setBooking(prev => {
            if (!prev) return prev;
            const prevRid = prev?.resourceId?._id || prev?.resourceId || null;
            if (String(prevRid || '') !== String(rid)) return prev;
            return { ...prev, resourceId: normalized };
          });
        } catch (_) {
          // ignore
        }
      };
      hydrateResource();
      return () => {
        canceled = true;
      };
    }

    const fetchBooking = async () => {
      if (effectiveBookingId) {
        setLoading(true);
        try {
          const fetchedBooking = await getInternalBookingById(effectiveBookingId);
          setBooking(fetchedBooking);
          setComment(fetchedBooking.comment || '');
          setOriginalComment(fetchedBooking.comment || '');
          setCustomerComment(fetchedBooking.customerComment || '');

          setPrepTimeMinutes(fetchedBooking.entertainmentId?.prepTimeMinutes || 0);
          setPrepTimeWarnings(fetchedBooking.entertainmentId?.prepTimeWarnings || []);

          // Set isEditable based on status/finished flag, force disable when pending online payment
          updateEditableFromBooking(fetchedBooking, hasUnlimitedEditRights);
        } catch (err) {
          toast.error('Fehler beim Laden der Buchung');
          console.error('Error fetching booking:', err);
        }
        setLoading(false);
      } else {
        setBooking(null);
      }
    };

    // Reset modal state before loading a booking.
    setComment('');
    setOriginalComment('');
    setEditingLineItem(null);
    setLineItemCount(null);
    setAvailableCapacity(0);
    setMinParticipants(null);
    setMaxParticipants(null);
    setOldLineItemCount(0);
    setCancelCountdown(0);
    setCountdownStart(null);
    setLockCancelBtn(false);
    setPresentCountdown(0);
    setPresentCountdownStart(null);
    setPaymentCountdown(0);
    setPaymentCountdownStart(null);
    setLockPaymentBtn(false);
    setCheckoutCountdown(0);
    setCheckoutCountdownStart(null);
    setLockCheckoutBtn(false);
    setSelectedRating(null);
    setCheckoutLoading(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableDates([]);
    setAvailableTimes([]);
    setTimesLoadedForDate(null);
    setCurrentMonth(dayjs().month() + 1);
    setCurrentYear(dayjs().year());
    setAvailablePackages([]);
    setPrepTimeMinutes(0);
    setPrepTimeWarnings([]);
    setEditingUpsell(null);
    setUpsellCount(null);
    setUpsellType(null);
    setEditingSpecialLineItem(null);
    setSpecialLineItemCount(null);
    setCurrentlyEditingField(null);
    setChangingGroupType(false);
    setGroupTypeConfirmOpen(false);
    setCategoryDialogOpen(false);
    setCategoryDialogCategories([]);
    setAddingCategory(false);
    setExternalInvoice(null);
    setExtPdfUrl(null);
    setExtPdfViewerOpen(false);
    setExtPaymentWarningOpen(false);
    setOpenExtInvoiceAfterPayment(false);
    fetchBooking();
  }, [open, bookingId, effectiveBookingId, bookingOverride, embedded]);

  // Leaving/re-opening the modal (or opening a different root booking) always
  // returns to the booking the modal was opened with.
  useEffect(() => {
    setSiblingView(null);
  }, [open, bookingId]);

  useEffect(() => {
    if (!booking?._id || !booking?.entertainmentId?.isExternallyManaged) return;
    let canceled = false;
    const loadExternalInvoice = async () => {
      try {
        const invoices = await getBookingInvoices(booking._id);
        if (canceled) return;
        const ext = (invoices || []).find(inv => inv.origin === 'externalPos' && inv.type === 'original');
        if (ext) setExternalInvoice(ext);
      } catch (_) {}
    };
    loadExternalInvoice();
    return () => { canceled = true; };
  }, [booking?._id, booking?.entertainmentId?.isExternallyManaged, getBookingInvoices]);

  useEffect(() => {
    if (!openExtInvoiceAfterPayment || !booking?.fullyPaid || !pendingExternalInvoicePayload) return;
    setOpenExtInvoiceAfterPayment(false);
    const payload = pendingExternalInvoicePayload;
    setPendingExternalInvoicePayload(null);
    const autoCreate = async () => {
      try {
        const invoice = await createExternalInvoice(payload);
        setExternalInvoice(invoice);
        toast.success('Bezahlte Rechnung erfolgreich erstellt.');
        try {
          const blob = await getInvoicePDFById(invoice._id);
          if (blob) {
            if (extPdfUrl) window.URL.revokeObjectURL(extPdfUrl);
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            setExtPdfUrl(url);
            setExtPdfViewerOpen(true);
          }
        } catch {
          toast.error('Fehler beim Anzeigen der Rechnung.');
        }
      } catch {
        toast.error('Fehler beim Erstellen der Rechnung.');
      }
    };
    autoCreate();
  }, [openExtInvoiceAfterPayment, booking?.fullyPaid, pendingExternalInvoicePayload]);

  // Fetch merge candidates once the booking is loaded and is a confirmed RV booking.
  // Depends on the loaded booking's identity/status fields to avoid firing before data is ready
  // and to skip the request entirely for non-RV or non-confirmed bookings.
  useEffect(() => {
    setVoucherMergeCandidates([]);
    if (
      bookingOverride ||
      !booking ||
      String(booking._id) !== String(bookingId) ||
      booking.status !== 'confirmed' ||
      booking.productModel !== 'ResourceVoucherProduct'
    ) return;
    const fetch = async () => {
      try {
        const candidates = await getVoucherMergeCandidates(bookingId);
        setVoucherMergeCandidates(Array.isArray(candidates) ? candidates : []);
      } catch (_) {
        setVoucherMergeCandidates([]);
      }
    };
    fetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?._id, booking?.status, booking?.productModel, bookingOverride]);

  // Periodically refresh the booking to reflect external changes (includes populated members)
  useEffect(() => {
    if (bookingOverride && typeof bookingOverride === 'object') return undefined;
    if (!open || !effectiveBookingId) return undefined;
    let canceled = false;
    const tick = async () => {
      try {
        const fresh = await getInternalBookingById(effectiveBookingId);
        if (canceled || !fresh) return;
        setBooking(fresh);
      } catch (_) {}
    };
    const intervalId = setInterval(tick, 10000);
    tick();
    return () => {
      canceled = true;
      clearInterval(intervalId);
    };
  }, [open, effectiveBookingId]);

  useEffect(() => {
    const enrich = async () => {
      try {
        const pm = booking?.ltSpecific?.participatingMembers || [];
        if (!booking || pm.length === 0) {
          setMemberDetailsMap(null);
          return;
        }
        const idsToFetch = Array.from(
          new Set(
            pm
              .filter(
                m =>
                  typeof m === 'string' ||
                  (m &&
                    typeof m === 'object' &&
                    typeof m._id === 'string' &&
                    (m.playerName === undefined || m.cardStatus === undefined))
              )
              .map(m => (typeof m === 'string' ? m : m._id))
          )
        );
        if (idsToFetch.length === 0) {
          setMemberDetailsMap(null);
          return;
        }
        const details = await getMembersByIds(idsToFetch);
        const map = new Map((details || []).map(d => [String(d._id), d]));
        setMemberDetailsMap(map);
      } catch (e) {
        // ignore
      }
    };
    enrich();
  }, [booking]);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;

    // periodically refresh member details and propagate into booking state
    const refreshMembers = async () => {
      const currentBooking = bookingRef.current;
      if (!currentBooking?._id) return;
      const pm = currentBooking?.ltSpecific?.participatingMembers || [];
      if (!Array.isArray(pm) || pm.length === 0) return;
      const ids = pm.map(m => (typeof m === 'string' ? m : m && m._id)).filter(Boolean);
      if (ids.length === 0) return;
      try {
        const details = await getMembersByIdsRef.current(ids);
        const byId = new Map((details || []).map(d => [String(d._id), d]));

        // update details map for chips
        setMemberDetailsMap(byId);

        // build enriched members array
        const enriched = pm.map(m => {
          const id = typeof m === 'string' ? m : m._id;
          const d = byId.get(String(id));
          if (d) return { _id: String(id), ...d };
          return typeof m === 'string' ? { _id: String(m) } : m;
        });

        if (!isActive) return;

        // push enriched members into booking state to propagate to child modals
        const nextBooking = {
          ...currentBooking,
          ltSpecific: {
            ...currentBooking.ltSpecific,
            participatingMembers: enriched,
          },
        };
        setBooking(nextBooking);
      } catch (e) {
        // ignore background refresh errors
      }
    };

    const intervalId = setInterval(refreshMembers, 10000);
    // run once immediately
    refreshMembers();

    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [open]);
  useEffect(() => {
    if (presentCountdown > 0) {
      const interval = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - presentCountdownStart) / 1000);
        const remainingTime = Math.max(0, 3 - elapsedSeconds);

        setPresentCountdown(remainingTime);

        if (remainingTime === 0) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [presentCountdown, presentCountdownStart]);

  useEffect(() => {
    if (paymentCountdown > 0) {
      const interval = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - paymentCountdownStart) / 1000);
        const remainingTime = Math.max(0, 3 - elapsedSeconds);

        setPaymentCountdown(remainingTime);

        if (remainingTime === 0) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [paymentCountdown, paymentCountdownStart]);

  useEffect(() => {
    if (cancelCountdown > 0) {
      const interval = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - countdownStart) / 1000);
        const remainingTime = Math.max(0, 3 - elapsedSeconds);

        setCancelCountdown(remainingTime);

        if (remainingTime === 0) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [cancelCountdown, countdownStart]);

  useEffect(() => {
    if (checkoutCountdown > 0) {
      const interval = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - checkoutCountdownStart) / 1000);
        const remainingTime = Math.max(0, 3 - elapsedSeconds);

        setCheckoutCountdown(remainingTime);

        if (remainingTime === 0) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [checkoutCountdown, checkoutCountdownStart]);

  // Handler: Modal close
  const handleClose = async () => {
    if (readOnly || (bookingOverride && typeof bookingOverride === 'object')) {
      onClose();
      return;
    }
    if (comment !== originalComment) {
      setSaving(true);
      try {
        const updated = await updateInternalBooking(
          booking._id,
          { comment },
          null,
          null,
          null,
          { baseBooking: booking }
        );
        if (updated) onUpdate(booking._id, { comment: comment });
      } catch (err) {
        // Concurrency conflicts already surface a yellow toast from the hook.
        if (!err?.isConcurrencyConflict) {
          toast.error('Fehler beim Speichern des Kommentars');
        }
      }
      setSaving(false);
    }

    // Reset all states when modal is closed
    setComment('');
    setOriginalComment('');
    setEditingLineItem(null);
    setLineItemCount(null);
    setAvailableCapacity(0);
    setMinParticipants(null);
    setMaxParticipants(null);
    setOldLineItemCount(0);
    setCancelCountdown(0);
    setCountdownStart(null);
    setLockCancelBtn(false);
    setPresentCountdown(0);
    setPresentCountdownStart(null);
    setPaymentCountdown(0);
    setPaymentCountdownStart(null);
    setLockPaymentBtn(false);
    setCheckoutCountdown(0);
    setCheckoutCountdownStart(null);
    setLockCheckoutBtn(false);
    setSelectedRating(null);
    setCheckoutLoading(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableDates([]);
    setAvailableTimes([]);
    setTimesLoadedForDate(null);
    setAvailablePackages([]);
    setEditingUpsell(null);
    setUpsellCount(null);
    setUpsellSelectionOpen(false);
    setAvailableUpsells([]);
    setSelectedLineItem(null);
    setLineItemPaymentModalOpen(false);
    setConvertingPayment(false);
    setIsEditable(true);
    setPrepTimeMinutes(0);
    setPrepTimeWarnings([]);
    setCurrentlyEditingField(null);
    setChangingGroupType(false);
    setGroupTypeConfirmOpen(false);
    setCategoryDialogOpen(false);
    setCategoryDialogCategories([]);
    setAddingCategory(false);

    onClose();
  };

  const handleCancellation = async () => {
    setLockCancelBtn(true);
    setCancelCountdown(0);
    const toastMessage =
      booking.status === 'blocked'
        ? `Die Stornierungsanfrage für den Zeitslotblocker am ${dayjs(booking.startTime).format(
            'DD.MM.YYYY'
          )} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden.`
        : `Die Stornierungsanfrage für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const successToastMessage = 'Buchung erfolgreich storniert';
    const displayInfo = {
      Typ: booking.status === 'blocked' ? 'Zeitslotblocker stornieren' : 'Buchung stornieren',
      Buchung:
        booking.status === 'blocked'
          ? `Zeitslotblocker am ${dayjs(booking.startTime).format('DD.MM.YYYY')}`
          : `${booking.firstName} ${booking.lastName}`,
      Slots: booking.laneCount || booking.participantCount,
    };
    const onStored = () => {
      onClose();
    };
    const onSuccess = () => {
      onUpdate(booking._id, { firstName: 'STORNIERT: ', status: 'canceled' });
      onClose();
    };
    try {
      const cancelResult =
        booking.status === 'blocked'
          ? await cancelBlocker(
              { bookingId: booking._id },
              toastMessage,
              onStored,
              displayInfo,
              onSuccess,
              successToastMessage
            )
          : await cancelBooking(
              { bookingId: booking._id },
              toastMessage,
              onStored,
              displayInfo,
              onSuccess,
              successToastMessage
            );
      if (cancelResult && cancelResult.success) {
        onSuccess();
      } else if (cancelResult && !cancelResult.success) {
        setLockCancelBtn(false);
      }
    } catch (error) {
      console.error('Fehler beim Stornieren der Buchung:', error);
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error('Fehler beim Stornieren der Buchung');
      }
      setLockCancelBtn(false);
    }
  };

  const handleRejectPending = async () => {
    setLockCancelBtn(true);
    setCancelCountdown(0);
    const toastMessage = `Die Ablehnungsanfrage für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann nach Identifizierung abgeschlossen werden`;
    const successToastMessage = 'Buchungsanfrage abgelehnt';
    const displayInfo = {
      Typ: 'Buchungsanfrage ablehnen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchungsnummer: booking.bookingNumber,
      Termin: dayjs(booking.startTime).format('DD.MM.YYYY HH:mm'),
    };
    const onStored = () => {
      setLockCancelBtn(false);
      onClose();
    };
    const onSuccessCallback = updatedBooking => {
      if (updatedBooking) {
        setBooking(updatedBooking);
        onUpdate(booking._id, updatedBooking);
        toast.success(successToastMessage);
        onClose();
      }
      setLockCancelBtn(false);
    };
    try {
      await rejectPendingBooking(
        booking._id,
        toastMessage,
        onStored,
        displayInfo,
        onSuccessCallback,
        successToastMessage
      );
    } catch (error) {
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        console.error('Error rejecting pending booking:', error);
      }
      setLockCancelBtn(false);
    }
  };

  const startEditingLineItem = async item => {
    if (Array.isArray(booking?.resourceVoucherIds) && booking.resourceVoucherIds.length > 0) {
      return;
    }
    // get available free space and participant constraints
    const capacityResult = await getAvailableCapacity({ bookingId: booking._id });
    if (capacityResult) {
      const baseAdditionalCapacity =
        typeof capacityResult.maxCapacity === 'number' && capacityResult.maxCapacity >= 0
          ? capacityResult.maxCapacity
          : null;
      const currentParticipants =
        typeof capacityResult.currentParticipants === 'number'
          ? capacityResult.currentParticipants
          : Number(booking?.participantCount || 0);
      const derivedAdditionalFromMaxParticipants =
        typeof capacityResult.maxParticipants === 'number'
          ? Math.max(0, capacityResult.maxParticipants - currentParticipants)
          : null;

      const additionalCapacityConstraints = [
        baseAdditionalCapacity,
        derivedAdditionalFromMaxParticipants,
      ].filter(v => typeof v === 'number' && v >= 0);
      const derivedAdditionalCapacity =
        additionalCapacityConstraints.length > 0 ? Math.min(...additionalCapacityConstraints) : 0;

      setAvailableCapacity(derivedAdditionalCapacity);
      setMinParticipants(capacityResult.minParticipants);
      setMaxParticipants(capacityResult.maxParticipants);
      setCanOverbook(Boolean(capacityResult.canOverbook));
      setOverbookEnabled(false);
      setEditingLineItem(item._id);
      setLineItemCount(item.count);
      setOldLineItemCount(item.count);
    }
  };

  const stopEditingLineItem = async () => {
    // Find the current lineItem based on the _id
    const currentLineItem = booking.lineItems.find(item => item._id === editingLineItem);

    if (!currentLineItem) {
      toast.error('Position nicht gefunden');
      console.error('Lineitem not found');
      return;
    }

    const categoryId = currentLineItem.categoryId;

    if (lineItemCount > oldLineItemCount) {
      // persons have been added
      const countToAdd = lineItemCount - oldLineItemCount;

      try {
        const latestCapacity = await getAvailableCapacity({ bookingId: booking._id });
        const baseAdditionalCapacity =
          typeof latestCapacity?.maxCapacity === 'number' && latestCapacity.maxCapacity >= 0
            ? latestCapacity.maxCapacity
            : null;
        const currentParticipants =
          typeof latestCapacity?.currentParticipants === 'number'
            ? latestCapacity.currentParticipants
            : Number(booking?.participantCount || 0);
        const derivedAdditionalFromMaxParticipants =
          typeof latestCapacity?.maxParticipants === 'number'
            ? Math.max(0, latestCapacity.maxParticipants - currentParticipants)
            : null;
        const additionalCapacityConstraints = [
          baseAdditionalCapacity,
          derivedAdditionalFromMaxParticipants,
        ].filter(v => typeof v === 'number' && v >= 0);
        const derivedAdditionalCapacity =
          additionalCapacityConstraints.length > 0 ? Math.min(...additionalCapacityConstraints) : 0;

        setAvailableCapacity(derivedAdditionalCapacity);
        setMinParticipants(latestCapacity?.minParticipants ?? null);
        setMaxParticipants(latestCapacity?.maxParticipants ?? null);

        if (countToAdd > derivedAdditionalCapacity && !overbookEnabled) {
          toast.error('Nicht genug Kapazität verfügbar. Bitte aktualisieren und erneut versuchen.');
          return;
        }
      } catch (e) {
        toast.error('Fehler beim Prüfen der Kapazität');
        console.error('Error checking capacity before addParticipants', e);
        return;
      }

      const toastMessage = `Die Anfrage für das Hinzufügen von ${
        countToAdd > 1 ? `${countToAdd} Teilnehmern` : 'einem Teilnehmer'
      } zur Buchung von ${booking.firstName} ${
        booking.lastName
      } wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
      const successToastMessage = `${
        countToAdd > 1 ? `${countToAdd} Teilnehmer` : 'Einen Teilnehmer'
      } erfolgreich zur Buchung von ${booking.firstName} ${booking.lastName} hinzugefügt`;
      const displayInfo = {
        Typ: 'Teilnehmer hinzufügen',
        Kunde: `${booking.firstName} ${booking.lastName}`,
        Buchungsdaten: `${dayjs(booking.startTime).format('DD.MM.YYYY')} ${dayjs(
          booking.startTime
        ).format('HH:mm')}`,
        Anzahl: countToAdd,
      };
      const onStored = () => {};
      const onSuccess = result => {
        if (result && result.booking) {
          setBooking(result.booking);
          onUpdate(booking._id, result.booking);
        }
      };

      try {
        const addResult = await addBookingParticipants(
          {
            bookingId: booking._id,
            categoryId,
            countToAdd,
            allowOverbook: overbookEnabled,
          },
          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );

        if (addResult) {
          onSuccess(addResult);
        }
      } catch (error) {
        const code = getErrorCode(error);
        if (code === 'GROUP_TYPE_CAPACITY_EXCEEDED') {
          const flipData = error?.data?.data || error?.data || {};
          const prevLabel = flipData.previousGroupType === 'kids' ? 'Kinder' : 'Erwachsene';
          const newLabel = flipData.newGroupType === 'kids' ? 'Kinder' : 'Erwachsene';
          setGroupTypeFlipDialog({
            message: `Durch das Hinzufügen würde die Gruppe von "${prevLabel}" zu "${newLabel}" wechseln. Die Kapazität im ${newLabel}-Pool reicht nicht aus (${flipData.requiredCapacity ?? '?'} Plätze benötigt, ${flipData.availableCapacity ?? '?'} frei). Möchtest du den bisherigen Gruppentyp "${prevLabel}" beibehalten?`,
            retryFn: async () => {
              try {
                const retryResult = await addBookingParticipants(
                  {
                    bookingId: booking._id,
                    categoryId,
                    countToAdd,
                    keepGroupType: true,
                    allowOverbook: overbookEnabled,
                  },
                  toastMessage,
                  onStored,
                  displayInfo,
                  onSuccess,
                  successToastMessage
                );
                if (retryResult) {
                  onSuccess(retryResult);
                }
              } catch (retryErr) {
                toast.error('Teilnehmer konnten nicht hinzugefügt werden');
                console.error('Error adding participants with keepGroupType', retryErr);
              }
            },
          });
        } else {
          toast.error('Teilnehmer konnten nicht hinzugefügt werden (Kapazität ggf. belegt)');
        }
        console.error('Error adding booking participants', error);
        try {
          const refreshed = await getInternalBookingById(booking._id);
          if (refreshed) {
            setBooking(refreshed);
            updateEditableFromBooking(refreshed, hasUnlimitedEditRights);
            onUpdate(booking._id, refreshed);
          }
        } catch (_) {}
      }
    } else if (lineItemCount < oldLineItemCount) {
      // persons have been removed
      const countToCancel = oldLineItemCount - lineItemCount;
      const toastMessage = `Die Anfrage für das Stornieren von ${
        countToCancel > 1 ? `${countToCancel} Teilnehmern` : 'einem Teilnehmer'
      } für die Buchung von ${booking.firstName} ${
        booking.lastName
      } wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
      const successToastMessage = `${
        countToCancel > 1 ? `${countToCancel} Teilnehmer` : 'Einen Teilnehmer'
      } erfolgreich für die Buchung von ${booking.firstName} ${booking.lastName} storniert`;
      const displayInfo = {
        Typ: 'Teilnehmer stornieren',
        Kunde: `${booking.firstName} ${booking.lastName}`,
        Buchungsdaten: `${dayjs(booking.startTime).format('DD.MM.YYYY')} ${dayjs(
          booking.startTime
        ).format('HH:mm')}`,
        Anzahl: countToCancel,
      };
      const onStored = () => {};
      const onSuccess = result => {
        if (result && result.booking) {
          setBooking(result.booking);
          onUpdate(booking._id, result.booking);
        }
      };
      try {
        const cancelResult = await cancelBookingParticipants(
          {
            bookingId: booking._id,
            categoryId,
            countToCancel,
          },

          toastMessage,
          onStored,
          displayInfo,
          onSuccess,
          successToastMessage
        );

        if (cancelResult) {
          onSuccess(cancelResult);
        }
      } catch (error) {
        const code = getErrorCode(error);
        if (code === 'UPGRADE_MINIMUM_COUNT_NOT_REACHED') {
          toast.error(
            'Reduzierung nicht möglich: Mindestteilnehmerzahl fuer gewaehltes Paket/Zusatzleistung unterschritten.'
          );
        } else if (code === 'GROUP_TYPE_CAPACITY_EXCEEDED') {
          const flipData = error?.data?.data || error?.data || {};
          const prevLabel = flipData.previousGroupType === 'kids' ? 'Kinder' : 'Erwachsene';
          const newLabel = flipData.newGroupType === 'kids' ? 'Kinder' : 'Erwachsene';
          setGroupTypeFlipDialog({
            message: `Durch die Stornierung würde die Gruppe von "${prevLabel}" zu "${newLabel}" wechseln. Die Kapazität im ${newLabel}-Pool reicht nicht aus (${flipData.requiredCapacity ?? '?'} Plätze benötigt, ${flipData.availableCapacity ?? '?'} frei). Möchtest du den bisherigen Gruppentyp "${prevLabel}" beibehalten?`,
            retryFn: async () => {
              try {
                const retryResult = await cancelBookingParticipants(
                  {
                    bookingId: booking._id,
                    categoryId,
                    countToCancel,
                    keepGroupType: true,
                  },
                  toastMessage,
                  onStored,
                  displayInfo,
                  onSuccess,
                  successToastMessage
                );
                if (retryResult) {
                  onSuccess(retryResult);
                }
              } catch (retryErr) {
                toast.error('Teilnehmer konnten nicht storniert werden');
                console.error('Error canceling participants with keepGroupType', retryErr);
              }
            },
          });
        } else if (code === 'SPONTANEOUS_REDUCTION_REQUIRED') {
          // Offer both paths straight from the modal instead of a dead-end hint: convert to a
          // MOP-show voucher (needs spontaneousReduction right, escalates if missing), or — for
          // staff allowed to override (unlimitedEdits / forceReduceLastMinute) — a free reduction.
          const gateData = error?.data?.data || error?.data || {};
          // A booked package/upsell minimum is not liftable by any right, so the free-reduction
          // option must not be offered for that reason — it would just fail again.
          const canForceReduce =
            gateData.reason === 'upgradeMinimum'
              ? false
              : !!gateData.canOverride || effectiveHasUnlimitedEditRightsUi;
          setReductionGateDialog({
            categoryId,
            countToCancel,
            canForceReduce,
            reason: gateData.reason || null,
            minimumCount: gateData.minimumCount || null,
            upgradeName: gateData.upgradeName || null,
          });
        } else {
          toast.error('Fehler beim Stornieren von Teilnehmern');
        }
        console.error('Error canceling booking participants', error);
      }
    }

    setEditingLineItem(null);
    setLineItemCount(null);
    setAvailableCapacity(0);
    setMinParticipants(null);
    setMaxParticipants(null);
  };

  const handleCancelClick = () => {
    if (cancelCountdown > 0) {
      // Second click, apply action
      if (booking?.status === 'awaitingConfirmation') {
        handleRejectPending();
      } else {
        handleCancellation();
      }
    } else {
      setCountdownStart(Date.now());
      setCancelCountdown(3);
    }
  };

  // Handler: Package add - open package selection dialog
  const handleAddPackageClick = async () => {
    try {
      const packages = await getAvailablePackages({ bookingId: booking._id });
      setAvailablePackages(packages);
      setPackageSelectionOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Pakete');
      console.error('Error fetching available packages', error);
    }
  };

  // Handler: Package select and add
  const handlePackageSelect = async upgradeId => {
    const selectedPackage = availablePackages.find(p => p.upgradeId === upgradeId);
    if (!selectedPackage) {
      console.error('Selected package not found');
      return;
    }
    const onSuccess = result => {
      if (result && result.booking) {
        setBooking(result.booking);
        onUpdate(booking._id, result.booking);
      }
    };
    try {
      const result = await updateBookingPackage(
        {
          bookingId: booking._id,
          upgradeId,
        },
        'Deine Anfrage für das Hinzufügen des Pakets ' +
          selectedPackage.upgrade.name +
          ' zur Buchung von ' +
          booking.firstName +
          ' ' +
          booking.lastName +
          ' wurde gespeichert.',
        null,
        {
          Typ: 'Paket hinzufügen',
          Kunde: `${booking.firstName} ${booking.lastName}`,
          Paket: selectedPackage.upgrade.name,
        },
        onSuccess,
        'Paket erfolgreich hinzugefügt.'
      );
      if (result) {
        onSuccess(result);
      }
      setPackageSelectionOpen(false);
    } catch (error) {
      const code = getErrorCode(error);
      if (code === 'UPGRADE_MINIMUM_COUNT_NOT_REACHED') {
        toast.error('Paket kann wegen Mindestteilnehmerzahl nicht hinzugefuegt werden.');
      } else {
        toast.error('Fehler beim Aktualisieren des Pakets');
      }
      console.error('Error updating package', error);
    }
  };

  // Handler: Package remove
  const handleRemovePackage = async item => {
    const onSuccess = result => {
      if (result && result.booking) {
        setBooking(result.booking); // Update local state
        onUpdate(booking._id, result.booking);
      }
    };
    try {
      const result = await updateBookingPackage(
        {
          bookingId: booking._id,
          upgradeId: null,
        },
        'Deine Anfrage für das Entfernen des Pakets ' +
          booking.selectedPackage.name +
          ' aus der Buchung von ' +
          booking.firstName +
          ' ' +
          booking.lastName +
          ' wurde gespeichert.',
        null,
        {
          Typ: 'Paket entfernen',
          Kunde: `${booking.firstName} ${booking.lastName}`,
          Paket: booking.selectedPackage.name,
        },
        onSuccess,
        'Paket erfolgreich entfernt.'
      );
      if (result) {
        onSuccess(result);
      }
    } catch (error) {
      toast.error('Fehler beim Entfernen des Pakets');
      console.error('Error removing package', error);
    }
  };

  // Handler: Open shift dialog
  const handleShiftClick = async () => {
    const initialDate = booking?.startTime ? dayjs(booking.startTime) : dayjs();
    const month = initialDate.month() + 1;
    const year = initialDate.year();

    setCurrentMonth(month);
    setCurrentYear(year);
    setSelectedDate(initialDate.toDate());
    setSelectedTime(null);
    setShiftOverbookEnabled(false);

    setShiftDialogOpen(true);
    setLoadingDates(true);
    await loadAvailableTimes(initialDate.toDate(), false);

    try {
      const datesResult = await getAvailableShiftDates({
        bookingId: booking._id,
        month,
        year,
        allowOverbook: false,
      });
      setAvailableDates(Array.isArray(datesResult) ? datesResult : []);
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren Daten');
      console.error('Error loading available shift dates', error);
    }
    setLoadingDates(false);
  };

  const loadAvailableTimes = async (date, allowOverbook = shiftOverbookEnabled) => {
    // Avoid fetching times again for the same date unless overbook toggle changed
    if (
      timesLoadedForDate &&
      dayjs(timesLoadedForDate.date).isSame(date, 'day') &&
      timesLoadedForDate.allowOverbook === allowOverbook
    ) {
      return;
    }

    setLoadingTimes(true);
    try {
      const timesResult = await getAvailableShiftTimes({
        bookingId: booking._id,
        date: dayjs(date).format('YYYY-MM-DD'),
        allowOverbook,
      });
      setAvailableTimes(Array.isArray(timesResult) ? timesResult : []);
      setTimesLoadedForDate({ date, allowOverbook });
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren Zeiten');
      console.error('Error loading available times', error);
    }
    setLoadingTimes(false);
  };

  const handleShiftOverbookToggle = async enabled => {
    setShiftOverbookEnabled(enabled);
    setLoadingDates(true);
    try {
      const datesResult = await getAvailableShiftDates({
        bookingId: booking._id,
        month: currentMonth,
        year: currentYear,
        allowOverbook: enabled,
      });
      setAvailableDates(Array.isArray(datesResult) ? datesResult : []);
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren Daten');
      console.error('Error loading available shift dates', error);
    }
    setLoadingDates(false);

    if (selectedDate) {
      setTimesLoadedForDate(null);
      await loadAvailableTimes(selectedDate, enabled);
    }
  };

  const handleDateSelect = async date => {
    setSelectedDate(date);
    setSelectedTime(null);
    if (date) {
      await loadAvailableTimes(date);
    } else {
      setAvailableTimes([]);
      setTimesLoadedForDate(null);
    }
  };

  const handleTimeSelect = time => {
    setSelectedTime(time);
  };

  const handleShiftConfirm = async () => {
    if (!selectedDate || !selectedTime) return;

    const newDateTime = dayjs(selectedDate)
      .hour(parseInt(selectedTime.split(':')[0]))
      .minute(parseInt(selectedTime.split(':')[1]))
      .format();

    const toastMessage = `Die Verschiebungsanfrage für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const successToastMessage = 'Buchung erfolgreich verschoben!';

    const displayInfo = {
      Typ: 'Buchung verschieben',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Von: `${dayjs(booking.startTime).format('DD.MM.YYYY')} ${dayjs(booking.startTime).format(
        'HH:mm'
      )}`,
      Nach: `${dayjs(newDateTime).format('DD.MM.YYYY')} ${dayjs(newDateTime).format('HH:mm')}`,
      Betrag: `${parseFloat(booking.totalAmount).toFixed(2).replace('.', ',')}€`,
    };

    const onStored = () => {
      // Close the modal when the request is stored due to authentication failure
      setShiftDialogOpen(false);
      onClose();
    };

    try {
      const result = await shiftBooking(
        {
          bookingId: booking._id,
          newDate: newDateTime,
          allowOverbook: shiftOverbookEnabled,
        },
        toastMessage,
        onStored,
        displayInfo,
        null,
        successToastMessage
      );

      if (result && result.booking) {
        const originalDate = dayjs(booking.startTime).format('YYYY-MM-DD');
        const newDate = dayjs(newDateTime).format('YYYY-MM-DD');

        if (originalDate === newDate) {
          // If the date stays the same, update the booking with new time
          setBooking(result.booking);
          onUpdate(booking._id, result.booking);
        } else {
          // If the date changes, remove the booking from current view and add to new date
          setBooking(result.booking);
          onUpdate(booking._id, { status: 'removed' });
          // The booking will appear in the view for the new date when it's loaded
        }
        setShiftDialogOpen(false);
        onClose(); // Schließe das Modal
      }
    } catch (error) {
      // Only show error if it's not an authentication failure
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        console.error('Error shifting booking', error);
      }
    }
  };

  const handleDateNavigation = async ({ month, year }) => {
    setCurrentMonth(month);
    setCurrentYear(year);
    setLoadingDates(true);
    try {
      const datesResult = await getAvailableShiftDates({
        bookingId: booking._id,
        month,
        year,
        allowOverbook: shiftOverbookEnabled,
      });
      setAvailableDates(Array.isArray(datesResult) ? datesResult : []);
      // Reset selected date and times when navigating
      setSelectedTime(null);
      setAvailableTimes([]);
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren Daten');
      console.error('Error loading available shift dates', error);
    }
    setLoadingDates(false);
  };

  const handlePresentClick = () => {
    if (presentCountdown > 0) {
      const now = new Date();
      const bookingStartTime = new Date(booking.startTime);
      const isLate = now > bookingStartTime - prepTimeMinutes * 60000;

      if (isLate) {
        setLateArrivalModalOpen(true);
      } else {
        handleMarkAsPresent();
      }
    } else {
      setPresentCountdownStart(Date.now());
      setPresentCountdown(3);
    }
  };

  const handleMarkAsPresent = async selectedLateReason => {
    setPresentCountdown(0);
    const result = await markCustomerAsPresent({
      bookingId: booking._id,
      body: {
        lateArrivalReason: selectedLateReason || null,
      },
    });
    if (result && result.updatedBooking) {
      const newBookingData = { ...result.updatedBooking };
      if (
        booking.resourceId &&
        newBookingData.resourceId &&
        typeof newBookingData.resourceId === 'string'
      ) {
        newBookingData.resourceId = booking.resourceId;
      }

      setBooking(newBookingData);
      updateEditableFromBooking(newBookingData, hasUnlimitedEditRights);
      onUpdate(booking._id, newBookingData);
      setLateArrivalModalOpen(false);
    }
  };

  const handleLateArrivalSelect = reason => {
    handleMarkAsPresent(reason);
  };

  const handleOpenRewindCheckinConfirm = () => {
    if (!booking?._id || !booking.presentSince || booking.finishedAt) return;
    setRewindCheckinConfirmOpen(true);
  };

  const handleCloseRewindCheckinConfirm = () => {
    if (rewindCheckinLoading) return;
    setRewindCheckinConfirmOpen(false);
  };

  const handleConfirmRewindCheckin = async () => {
    if (!booking?._id || !booking.presentSince || booking.finishedAt) return;
    setRewindCheckinLoading(true);

    const toastMessage = `Die Anfrage, den Check-in für ${booking.firstName} ${booking.lastName} zurückzusetzen, wurde gespeichert`;
    const successToastMessage = `Der Check-in für ${booking.firstName} ${booking.lastName} wurde zurückgesetzt`;
    const displayInfo = {
      Typ: 'Check-in zurücksetzen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchung: booking.bookingNumber || booking._id,
    };

    const onSuccess = result => {
      if (result && result.updatedBooking) {
        const newBookingData = { ...result.updatedBooking };
        if (
          booking.resourceId &&
          newBookingData.resourceId &&
          typeof newBookingData.resourceId === 'string'
        ) {
          newBookingData.resourceId = booking.resourceId;
        }
        // Reset checkout UI state because checkout becomes available again immediately
        setLockCheckoutBtn(false);
        setCheckoutLoading(false);
        setCheckoutDialogOpen(false);
        setSelectedRating(null);
        setCheckoutCountdown(0);
        setCheckoutCountdownStart(null);
        setBooking(newBookingData);
        updateEditableFromBooking(newBookingData, hasUnlimitedEditRights);
        onUpdate(booking._id, newBookingData);
      }
    };

    try {
      const result = await rewindCheckin(
        { bookingId: booking._id },
        toastMessage,
        null,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      setRewindCheckinConfirmOpen(false);
      if (result) onSuccess(result);
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen des Check-ins');
      console.error('Error rewinding check-in', error);
    } finally {
      setRewindCheckinLoading(false);
    }
  };

  const handleOpenRewindCheckoutConfirm = () => {
    if (!booking?._id || !booking.finishedAt) return;
    setRewindCheckoutConfirmOpen(true);
  };

  const handleCloseRewindCheckoutConfirm = () => {
    if (rewindCheckoutLoading) return;
    setRewindCheckoutConfirmOpen(false);
  };

  const handleConfirmRewindCheckout = async () => {
    if (!booking?._id || !booking.finishedAt) return;
    setRewindCheckoutLoading(true);

    const toastMessage = `Die Anfrage, den Checkout für ${booking.firstName} ${booking.lastName} zurückzusetzen, wurde gespeichert`;
    const successToastMessage = `Der Checkout für ${booking.firstName} ${booking.lastName} wurde zurückgesetzt`;
    const displayInfo = {
      Typ: 'Checkout zurücksetzen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchung: booking.bookingNumber || booking._id,
    };

    const onSuccess = result => {
      if (result && result.updatedBooking) {
        const newBookingData = { ...result.updatedBooking };
        if (
          booking.resourceId &&
          newBookingData.resourceId &&
          typeof newBookingData.resourceId === 'string'
        ) {
          newBookingData.resourceId = booking.resourceId;
        }
        setBooking(newBookingData);
        updateEditableFromBooking(newBookingData, hasUnlimitedEditRights);
        onUpdate(booking._id, newBookingData);
      }
    };

    try {
      const result = await rewindCheckout(
        { bookingId: booking._id },
        toastMessage,
        null,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      setRewindCheckoutConfirmOpen(false);
      if (result) onSuccess(result);
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen des Checkouts');
      console.error('Error rewinding checkout', error);
    } finally {
      setRewindCheckoutLoading(false);
    }
  };

  const handlePaymentClick = async () => {
    if (paymentCountdown > 0) {
      setLockPaymentBtn(true);
      setPaymentCountdown(0);
      setPaymentModalOpen(false);

      const toastMessage = `Die Anfrage, die Buchung für ${booking.firstName} ${booking.lastName} als bezahlt zu markieren, wurde gespeichert`;
      const successToastMessage = `Die Buchung für ${booking.firstName} ${booking.lastName} wurde erfolgreich als bezahlt markiert`;
      const displayInfo = {
        Typ: 'Buchung als bezahlt markieren',
        Kunde: `${booking.firstName} ${booking.lastName}`,
        Betrag: `${parseFloat(booking.totalAmount).toFixed(2).replace('.', ',')}€`,
      };

      const onSuccess = result => {
        if (result && result.updatedBooking) {
          const newBookingData = { ...result.updatedBooking };
          if (
            booking.resourceId &&
            newBookingData.resourceId &&
            typeof newBookingData.resourceId === 'string'
          ) {
            newBookingData.resourceId = booking.resourceId;
          }
          setBooking(newBookingData);
          onUpdate(booking._id, newBookingData);
          setLockPaymentBtn(false);
        }
      };

      try {
        const result = await markBookingAsFullyPaid(
          { bookingId: booking._id },
          toastMessage,
          null, // onStored
          displayInfo,
          onSuccess,
          successToastMessage
        );
        if (result) {
          onSuccess(result);
        }
      } catch (error) {
        const code = getErrorCode(error);
        if (code === 'BOOKING_HAS_UNPAID_ONLINE_PAYMENTS') {
          toast.error('Offene Online-Zahlung zuerst zu Offline-Zahlung konvertieren.');
        } else {
          toast.error('Fehler beim Markieren der Buchung');
        }
        console.error('Error marking booking as paid', error);
        setLockPaymentBtn(false);
      }
    } else {
      setPaymentCountdownStart(Date.now());
      setPaymentCountdown(3);
    }
  };

  // mark a single line item (position) as paid
  const handleMarkLineItemPaid = async lineItem => {
    if (!lineItem || !lineItem._id) return;
    setLockPaymentBtn(true);

    const unpaidOfflineUnits = (lineItem.paymentReferences || [])
      .filter(ref => !ref.isPaid && ref.paymentType === 'offline')
      .reduce((total, ref) => total + ref.count, 0);

    const toastMessage = `Die Anfrage, die Position "${lineItem.description}" als bezahlt zu markieren, wurde gespeichert`;
    const successToastMessage = `Die Position "${lineItem.description}" wurde erfolgreich als bezahlt markiert`;
    const displayInfo = {
      Typ: 'Position als bezahlt markieren',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Position: lineItem.description,
      Betrag: `${parseFloat(lineItem.price * unpaidOfflineUnits)
        .toFixed(2)
        .replace('.', ',')}€`,
    };

    const onSuccess = result => {
      if (result && result.updatedBooking) {
        const newBookingData = { ...result.updatedBooking };
        if (
          booking.resourceId &&
          newBookingData.resourceId &&
          typeof newBookingData.resourceId === 'string'
        ) {
          newBookingData.resourceId = booking.resourceId;
        }
        setBooking(newBookingData);
        onUpdate(booking._id, newBookingData);

        // close the dialog only once there are no unpaid offline positions left
        const stillHasOpenPositions = (newBookingData.lineItems || []).some(
          item =>
            item.status === 'active' &&
            (item.paymentReferences || []).some(
              ref => !ref.isPaid && ref.paymentType === 'offline' && ref.count > 0
            )
        );
        if (!stillHasOpenPositions) {
          setPaymentModalOpen(false);
        }
      }
      setLockPaymentBtn(false);
    };

    try {
      const result = await markLineItemsAsPaid(
        { bookingId: booking._id, lineItemIds: [lineItem._id] },
        toastMessage,
        null, // onStored
        displayInfo,
        onSuccess,
        successToastMessage
      );
      if (result) {
        onSuccess(result);
      }
    } catch (error) {
      const code = getErrorCode(error);
      if (code === 'BOOKING_HAS_UNPAID_ONLINE_PAYMENTS') {
        toast.error('Offene Online-Zahlung zuerst zu Offline-Zahlung konvertieren.');
      } else {
        toast.error('Fehler beim Markieren der Position');
      }
      console.error('Error marking line item as paid', error);
      setLockPaymentBtn(false);
    }
  };

  const handleCheckoutClick = () => {
    if (checkoutCountdown > 0) {
      setCheckoutDialogOpen(true);
    } else {
      setCheckoutCountdownStart(Date.now());
      setCheckoutCountdown(3);
    }
  };

  const runCheckout = async () => {
    if (!selectedRating) return;

    setLockCheckoutBtn(true);
    setCheckoutLoading(true);
    try {
      const result = await markBookingAsFinished({
        bookingId: booking._id,
        experienceRating: selectedRating,
      });
      if (result && result.updatedBooking) {
        setBooking(result.updatedBooking);
        onUpdate(booking._id, result.updatedBooking);
        setBadRatingConfirmOpen(false);
        setCheckoutDialogOpen(false);
        setSelectedRating(null);
        setCheckoutCountdown(0);
      }
    } catch (error) {
      toast.error('Fehler beim Abschließen der Buchung');
      console.error('Error marking booking as finished:', error);
      setLockCheckoutBtn(false);
    }
    setCheckoutLoading(false);
  };

  // A "bad" rating mails the guests an apology and files a complaint for the
  // management, so it takes a second explicit confirmation before it is sent.
  const handleCheckout = () => {
    if (!selectedRating) return;
    if (selectedRating === 'bad') {
      setBadRatingConfirmOpen(true);
      return;
    }
    runCheckout();
  };

  const handleAddUpsellClick = async () => {
    try {
      const upsells = await getAvailableUpsells({
        productId: booking.productId,
        tags: booking.tags || [],
        bookingId: booking._id,
      });
      setAvailableUpsells(upsells);
      setUpsellSelectionOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren Upsells');
      console.error('Error fetching available upsells', error);
    }
  };

  const handleGroupTypeBadgeClick = async () => {
    if (!booking?.ltSpecific?.groupType) return;
    setGroupTypeCapacityInfo(null);
    setLoadingGroupTypeCapacity(true);
    setGroupTypeConfirmOpen(true);
    try {
      const capacityResult = await getAvailableCapacity({ bookingId: booking._id });
      if (capacityResult?.ageSplit) {
        setGroupTypeCapacityInfo(capacityResult.ageSplit);
      }
    } catch (err) {
      console.error('Error fetching capacity for group type change:', err);
    }
    setLoadingGroupTypeCapacity(false);
  };

  const handleChangeGroupType = async () => {
    if (!booking?.ltSpecific?.groupType) return;
    setGroupTypeConfirmOpen(false);
    setChangingGroupType(true);
    try {
      const newGroupType = booking.ltSpecific.groupType === 'kids' ? 'adults' : 'kids';
      const result = await updateInternalBooking(
        booking._id,
        { ltSpecific: { groupType: newGroupType } },
        null,
        null,
        null,
        { baseBooking: booking }
      );
      if (result) {
        const updatedBooking = result.booking || result;
        setBooking(updatedBooking);
        onUpdate(booking._id, updatedBooking);
        toast.success(
          `Gruppentyp auf ${newGroupType === 'kids' ? 'Kinder' : 'Erwachsene'} geändert`
        );
      }
    } catch (error) {
      if (error?.code === 'GROUP_TYPE_CAPACITY_EXCEEDED') {
        const flipData = error?.data?.data || error?.data || {};
        const newLabel = flipData.newGroupType === 'kids' ? 'Kinder' : 'Erwachsene';
        toast.error(
          `Nicht genügend Kapazität im ${newLabel}-Pool (${flipData.requiredCapacity ?? '?'} Plätze benötigt, ${flipData.availableCapacity ?? '?'} frei)`
        );
      } else if (error?.isConcurrencyConflict) {
        // Yellow conflict toast already shown by the hook.
      } else {
        toast.error('Fehler beim Ändern des Gruppentyps');
      }
      console.error('Error changing group type:', error);
    }
    setChangingGroupType(false);
  };

  const handleAddCategoryClick = async () => {
    try {
      if (addableCategories.length === 0) {
        toast.info('Alle verfügbaren Personenkategorien sind bereits vorhanden');
        return;
      }
      setCategoryDialogCategories(addableCategories);
      setSelectedCategoryName(addableCategories[0]?.name || '');
      setSelectedCategoryCount(1);
      setCategoryDialogOverbookEnabled(false);

      let ageSplitInfo = null;
      try {
        const capacityResult = await getAvailableCapacity({ bookingId: booking._id });
        if (capacityResult?.ageSplit) {
          ageSplitInfo = capacityResult.ageSplit;
        }
      } catch (_) {}
      setCategoryDialogAgeSplit(ageSplitInfo);

      setCategoryDialogOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Kategorien');
      console.error('Error loading categories:', error);
    }
  };

  const handleAddCategoryConfirm = async () => {
    if (!selectedCategoryName || selectedCategoryCount < 1) return;
    setAddingCategory(true);
    try {
      const result = await addBookingCategory(
        {
          bookingId: booking._id,
          categoryName: selectedCategoryName,
          count: selectedCategoryCount,
          allowOverbook: categoryDialogOverbookEnabled,
        },
        'Fehler beim Hinzufügen der Personenkategorie',
        null,
        {
          Typ: 'Personenkategorie hinzufügen',
          Kunde: `${booking.firstName} ${booking.lastName}`,
          Kategorie: selectedCategoryName,
          Anzahl: selectedCategoryCount,
        },
        null,
        'Personenkategorie erfolgreich hinzugefügt'
      );
      if (result && result.booking) {
        setBooking(result.booking);
        onUpdate(booking._id, result.booking);
      }
      setCategoryDialogOpen(false);
    } catch (error) {
      if (error?.code === 'GROUP_TYPE_CAPACITY_EXCEEDED') {
        const flipData = error?.data?.data || error?.data || {};
        const prevLabel = flipData.previousGroupType === 'kids' ? 'Kinder' : 'Erwachsene';
        const newLabel = flipData.newGroupType === 'kids' ? 'Kinder' : 'Erwachsene';
        const capturedCategoryName = selectedCategoryName;
        const capturedCategoryCount = selectedCategoryCount;
        setGroupTypeFlipDialog({
          message: `Durch das Hinzufügen der Kategorie würde die Gruppe von "${prevLabel}" zu "${newLabel}" wechseln. Die Kapazität im ${newLabel}-Pool reicht nicht aus (${flipData.requiredCapacity ?? '?'} Plätze benötigt, ${flipData.availableCapacity ?? '?'} frei). Möchtest du den bisherigen Gruppentyp "${prevLabel}" beibehalten?`,
          retryFn: async () => {
            try {
              const retryResult = await addBookingCategory(
                {
                  bookingId: booking._id,
                  categoryName: capturedCategoryName,
                  count: capturedCategoryCount,
                  keepGroupType: true,
                  allowOverbook: categoryDialogOverbookEnabled,
                },
                'Fehler beim Hinzufügen der Personenkategorie',
                null,
                {
                  Typ: 'Personenkategorie hinzufügen',
                  Kunde: `${booking.firstName} ${booking.lastName}`,
                  Kategorie: capturedCategoryName,
                  Anzahl: capturedCategoryCount,
                },
                null,
                'Personenkategorie erfolgreich hinzugefügt'
              );
              if (retryResult && retryResult.booking) {
                setBooking(retryResult.booking);
                onUpdate(booking._id, retryResult.booking);
              }
              setCategoryDialogOpen(false);
            } catch (retryErr) {
              toast.error('Fehler beim Hinzufügen der Personenkategorie');
              console.error('Error adding category with keepGroupType', retryErr);
            }
          },
        });
      } else if (error?.code === 'ADD_CATEGORY_CAPACITY_EXCEEDED') {
        toast.error('Nicht genügend Kapazität für diese Personenkategorie');
      } else {
        toast.error('Fehler beim Hinzufügen der Personenkategorie');
      }
      console.error('Error adding category:', error);
    }
    setAddingCategory(false);
  };

  const handleUpsellSelect = async upsellId => {
    const onSuccess = result => {
      if (result && result.booking) {
        setBooking(result.booking);
        onUpdate(booking._id, result.booking);
      }
    };
    try {
      // Find the selected upsell to check its type
      const selectedUpsell = availableUpsells.find(upsell => upsell.upgradeId === upsellId);

      // Determine the count based on upsell type
      let count = 1; // Default count is 1
      const upgradeType = String(selectedUpsell?.upgrade?.type || '').toLowerCase();
      if (upgradeType === 'matchpersons' || upgradeType === 'matchpersonsnocancel') {
        count = booking.participantCount;
      } else if (upgradeType === 'matchlanesnocancel') {
        const laneCountRaw = Number(booking?.laneCount || 0);
        const laneCount = Number.isFinite(laneCountRaw) && laneCountRaw > 0 ? laneCountRaw : null;
        if (laneCount !== null) {
          count = laneCount;
        } else {
          const persons = Math.max(0, Number(booking?.participantCount || 0));
          const densityRaw = Number(
            booking?.participantLaneDensity || booking?.entertainmentId?.participantLaneDensity || 0
          );
          const density = Number.isFinite(densityRaw) && densityRaw > 0 ? densityRaw : null;
          count = density ? Math.ceil(persons / density) : persons;
        }
      }

      const result = await addUpsell(
        {
          bookingId: booking._id,
          upsellId,
          count,
        },
        'Deine Anfrage für das Hinzufügen von ' +
          selectedUpsell.upgrade.name +
          ' zur Buchung von ' +
          booking.firstName +
          ' ' +
          booking.lastName +
          ' wurde gespeichert.',
        null,
        {
          Typ: 'Upsell hinzufügen',
          Kunde: `${booking.firstName} ${booking.lastName}`,
          Upsell: selectedUpsell.upgrade.name,
          Anzahl: count,
        },
        onSuccess,
        'Upsell erfolgreich hinzugefügt.'
      );
      if (result) {
        onSuccess(result);
      }
      setUpsellSelectionOpen(false);
    } catch (error) {
      const code = getErrorCode(error);
      if (code === 'UPGRADE_MINIMUM_COUNT_NOT_REACHED') {
        toast.error('Zusatzleistung kann wegen Mindestteilnehmerzahl nicht hinzugefuegt werden.');
      } else {
        toast.error('Fehler beim Hinzufügen des Upsells');
      }
      console.error('Error adding upsell', error);
    }
  };

  const startEditingUpsell = async (item, upsellType) => {
    setEditingUpsell(item._id);
    setUpsellCount(item.count);
    setUpsellType(upsellType);
  };

  const handleUpdatePhone = value => handleUpdateField('phone', value);

  const handleUpdateField = async (field, value) => {
    try {
      const updatedBooking = await updateBookingField({
        bookingId: booking._id,
        field,
        value,
      });
      if (updatedBooking) {
        setBooking(updatedBooking);
        onUpdate(booking._id, { [field]: value });
      }
    } catch (error) {
      toast.error(`Fehler beim Aktualisieren`);
      console.error(`Error updating ${field}:`, error);
      throw error;
    }
  };

  const handleUpdateFirstName = value => handleUpdateField('firstName', value);
  const handleUpdateLastName = value => handleUpdateField('lastName', value);
  const handleUpdateMail = value => handleUpdateField('mail', value);
  const handleUpdateCompanyName = value => handleUpdateField('companyName', value);
  const handleUpdateTableNumbers = tableNumbers =>
    handleUpdateField('tableNumbers', Array.isArray(tableNumbers) ? tableNumbers : []);

  const handleOverrideLineItemPrice = async (lineItemId, price) => {
    try {
      const updatedBooking = await overrideLineItemPrice(booking._id, lineItemId, price);
      if (updatedBooking?.booking) {
        setBooking(updatedBooking.booking);
        onUpdate(booking._id, {});
      }
    } catch (error) {
      toast.error('Fehler beim Überschreiben des Preises');
      console.error('Error overriding line item price:', error);
      throw error;
    }
  };

  const handleAddSpecialLineItem = async (description, count, price) => {
    try {
      const updatedBooking = await addSpecialLineItem(booking._id, description, count, price);
      if (updatedBooking?.booking) {
        setBooking(updatedBooking.booking);
        onUpdate(booking._id, {});
      }
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Sonder-Line-Items');
      console.error('Error adding special line item:', error);
      throw error;
    }
  };

  const handleUpdateCapacityOverride = async ({ capacityParticipantCount, capacityLaneCount }) => {
    if (!booking?._id) return;
    try {
      const patch = {};
      if (capacityParticipantCount !== undefined) {
        patch.capacityParticipantCount =
          capacityParticipantCount !== null && Number.isFinite(Number(capacityParticipantCount))
            ? Number(capacityParticipantCount)
            : null;
      }
      if (capacityLaneCount !== undefined) {
        patch.capacityLaneCount =
          capacityLaneCount !== null && Number.isFinite(Number(capacityLaneCount))
            ? Number(capacityLaneCount)
            : null;
      }
      if (Object.keys(patch).length === 0) return;
      const updated = await updateInternalBooking(
        booking._id,
        patch,
        null,
        null,
        null,
        { baseBooking: booking }
      );
      if (updated) {
        setBooking(updated);
        onUpdate(booking._id, updated);
        toast.success('Kapazitätsübersteuerung gespeichert');
      }
    } catch (err) {
      // Concurrency conflicts already surface a yellow toast from the hook.
      if (err?.message !== 'INSUFFICIENT_RIGHTS' && !err?.isConcurrencyConflict) {
        toast.error('Fehler beim Speichern der Kapazitätsübersteuerung');
      }
      throw err;
    }
  };

  const openCapacityOverrideDialog = () => {
    setCapacityOverrideEditParticipant(
      typeof booking?.capacityParticipantCount === 'number'
        ? String(booking.capacityParticipantCount)
        : ''
    );
    setCapacityOverrideEditLane(
      typeof booking?.capacityLaneCount === 'number' ? String(booking.capacityLaneCount) : ''
    );
    setCapacityOverrideDialogOpen(true);
  };

  const saveCapacityOverride = async () => {
    setCapacityOverrideSaving(true);
    try {
      const participantVal = capacityOverrideEditParticipant.trim();
      const laneVal = capacityOverrideEditLane.trim();
      await handleUpdateCapacityOverride({
        capacityParticipantCount: participantVal
          ? (Number(participantVal) >= 0 ? Number(participantVal) : null)
          : null,
        capacityLaneCount: laneVal ? (Number(laneVal) >= 0 ? Number(laneVal) : null) : null,
      });
      setCapacityOverrideDialogOpen(false);
    } finally {
      setCapacityOverrideSaving(false);
    }
  };

  const openResendConfirm = () => {
    setResendConfirmOpen(true);
  };

  const closeResendConfirm = () => {
    setResendConfirmOpen(false);
  };

  const handleResendConfirmation = async () => {
    if (!booking?._id) {
      return;
    }

    closeResendConfirm();
    setResendingConfirmation(true);
    const toastMessage = `Die Anfrage, die Bestätigung für ${booking.firstName} ${booking.lastName} erneut zu senden, wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const displayInfo = {
      Typ: 'Bestätigung erneut senden',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchungsnummer: booking.bookingNumber,
    };
    try {
      const response = await resendBookingConfirmation(
        booking._id,
        toastMessage,
        null,
        displayInfo,
        null,
        'Bestätigung wurde erneut gesendet.'
      );

      if (response) {
        toast.success('Bestätigung wurde erneut gesendet.');
      }
    } catch (error) {
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error('Fehler beim erneuten Senden der Bestätigung');
      }
      console.error('Error resending booking confirmation:', error);
    } finally {
      setResendingConfirmation(false);
    }
  };

  // send (or re-send) the payment link ("Bezahllink") by mail
  const handleSendPaymentLink = async () => {
    if (!booking?._id) {
      return;
    }

    setSendingPaymentLink(true);
    const toastMessage = `Die Anfrage, den Bezahllink für ${booking.firstName} ${booking.lastName} zu senden, wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const displayInfo = {
      Typ: 'Bezahllink senden',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchungsnummer: booking.bookingNumber,
    };
    try {
      const response = await sendPaymentLink(booking._id, toastMessage, null, displayInfo);
      setSendPaymentLinkOpen(false);

      if (response) {
        toast.success(
          response.switchedToOnline
            ? 'Buchung auf Online-Zahlung umgestellt und Bezahllink gesendet.'
            : 'Bezahllink wurde gesendet.'
        );
        // Refresh the booking data (the switch case changes payment references and fee)
        const updatedBooking = await getInternalBookingById(effectiveBookingId);
        setBooking(updatedBooking);
        onUpdate(effectiveBookingId, updatedBooking);
        updateEditableFromBooking(updatedBooking, hasUnlimitedEditRights);
      }
    } catch (error) {
      const code = error?.code;
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error(
          code === 'TOO_CLOSE_TO_START'
            ? 'Zu kurz vor dem Termin – Umstellung auf Online-Zahlung ist nicht mehr möglich.'
            : code === 'VOUCHER_APPLIED'
              ? 'Buchung mit eingelöstem Wertgutschein – Umstellung auf Online-Zahlung nicht möglich.'
              : code === 'BOOKING_ALREADY_PAID' || code === 'NOTHING_TO_PAY'
                ? 'Buchung ist bereits vollständig bezahlt.'
                : code === 'BOOKING_HAS_NO_MAIL'
                  ? 'Buchung hat keine E-Mail-Adresse.'
                  : code === 'BOOKING_STATUS_NOT_ALLOWED'
                    ? 'Buchung ist in diesem Status nicht für Online-Zahlung geeignet.'
                    : 'Fehler beim Senden des Bezahllinks'
        );
      }
      console.error('Error sending booking payment link:', error);
    } finally {
      setSendingPaymentLink(false);
    }
  };

  const handleInitiateCall = async () => {
    if (!booking?._id || !booking?.phone || !callCtx?.selectedAgent?.id) return;
    try {
      const response = await callCtx.initiateDial({
        agentId: callCtx.selectedAgent.id,
        destination: booking.phone,
        bookingId: booking._id,
      });
      if (response?.success) {
        toast.success('Anruf ausgelöst.');
      }
    } catch (error) {
      toast.error('Fehler beim Auslösen des Anrufs.');
      throw error;
    }
  };

  const handleFieldEditStart = fieldName => {
    console.log('BookingDetailsModal: Starting edit for field:', fieldName);
    setCurrentlyEditingField(fieldName);
  };

  const handleFieldEditEnd = () => {
    console.log('BookingDetailsModal: Ending edit for field:', currentlyEditingField);
    setCurrentlyEditingField(null);
  };

  const stopEditingUpsell = async () => {
    const onSuccess = result => {
      if (result && result.booking) {
        setBooking(result.booking);
        onUpdate(booking._id, result.booking);
      }
    };
    try {
      // Find the current lineItem to get its referenceId
      const currentLineItem = booking.lineItems.find(item => item._id === editingUpsell);

      if (!currentLineItem) {
        toast.error('Kein lineItem mit der angegebenen ID gefunden');
        console.error('Lineitem not found');
        return;
      }

      if (upsellCount !== currentLineItem.count) {
        const result = await updateUpsells(
          {
            bookingId: booking._id,
            upsellId: currentLineItem.referenceId,
            count: upsellCount,
          },
          'Deine Anfrage für das Aktualisieren der Anzahl von ' +
            booking.upsells.find(upsell => upsell.upgradeId === currentLineItem.referenceId).name +
            ' zur Buchung von ' +
            booking.firstName +
            ' ' +
            booking.lastName +
            ' wurde gespeichert.',
          null,
          {
            Typ: 'Upsell aktualisieren',
            Kunde: `${booking.firstName} ${booking.lastName}`,
            Upsell: booking.upsells.find(upsell => upsell.upgradeId === currentLineItem.referenceId)
              .name,
            Anzahl: upsellCount,
          },
          onSuccess,
          'Upsell erfolgreich aktualisiert.'
        );

        if (result) {
          onSuccess(result);
        }
      }

      setEditingUpsell(null);
      setUpsellCount(null);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Upsells');
      console.error('Error updating upsell count', error);
    }
  };

  const handleOpenSpontaneousReduction = () => {
    setSpontaneousReductionInitialCount(null);
    setSpontaneousReductionOpen(true);
  };
  const handleCloseSpontaneousReduction = () => {
    if (spontaneousReducing) return;
    setSpontaneousReductionOpen(false);
  };
  // Fetch the exact MOP-show voucher value from the backend (it deducts the product-linked ticket
  // insurance, which the frontend booking payload does not carry) so the dialog can show a precise
  // amount instead of a local estimate.
  const fetchSpontaneousReductionPreview = async (categoryId, count) => {
    if (!booking?._id || !count) return null;
    return previewSpontaneousReduction({
      bookingId: booking._id,
      categoryId,
      countToCancel: count,
    });
  };
  const handleConfirmSpontaneousReduction = async (count, categoryId, voucherMode = 'single') => {
    if (!booking || !categoryId || !count) return;
    setSpontaneousReducing(true);
    try {
      const result = await spontaneousReduction({
        bookingId: booking._id,
        categoryId,
        countToCancel: count,
        voucherMode,
      });
      // Refetch the fully-populated booking (memberSlots / participatingMembers) instead of using the
      // raw handler response, whose member references are not populated — applying that directly
      // briefly renders the member-card section as if all player cards were already present.
      let refreshed = null;
      try {
        refreshed = await getInternalBookingById(booking._id);
      } catch (_) {
        refreshed = null;
      }
      const nextBooking = refreshed || result?.booking || null;
      if (nextBooking) {
        setBooking(nextBooking);
        onUpdate(booking._id, nextBooking);
      }
      const issuedVouchers =
        Array.isArray(result?.vouchers) && result.vouchers.length > 0
          ? result.vouchers
          : result?.voucher
            ? [result.voucher]
            : [];
      if (result?.deferred) {
        // Booking not fully paid yet: the MOP-Show voucher is only issued once it is marked as paid.
        toast.success(
          'Reduktion vorgemerkt. Der MOP-Show-Gutschein wird ausgestellt, sobald die Buchung als vollständig bezahlt markiert ist.'
        );
      } else if (issuedVouchers.length > 1) {
        toast.success(
          `Reduktion erfolgreich. ${issuedVouchers.length} MOP-Show-Gutscheine erstellt: ${issuedVouchers
            .map(v => v.voucherCode)
            .join(', ')}.`
        );
      } else {
        const code = issuedVouchers[0]?.voucherCode;
        const value = issuedVouchers[0]?.value ?? result?.voucher?.value;
        toast.success(
          code
            ? `Reduktion erfolgreich. MOP-Show-Gutschein ${code}${
                value ? ` über ${Number(value).toFixed(2)} €` : ''
              } erstellt.`
            : 'Reduktion erfolgreich.'
        );
      }
      setSpontaneousReductionOpen(false);
    } catch (error) {
      console.error('Error during spontaneous reduction', error);
      toast.error('Fehler bei der spontanen Reduktion.');
    }
    setSpontaneousReducing(false);
  };

  // Goodwill cancellation: cancel without refund, the paid amount (minus ticket insurance)
  // is issued as a goodwill voucher. The dialog fetches the settlement preview beforehand.
  const fetchGoodwillCancellationPreview = async () => {
    if (!booking?._id) return null;
    return previewGoodwillCancellation({ bookingId: booking._id });
  };
  const handleConfirmGoodwillCancellation = async () => {
    if (!booking) return;
    setGoodwillCanceling(true);
    try {
      const result = await cancelBookingWithGoodwillVoucher({ bookingId: booking._id });
      const code = result?.voucher?.voucherCode;
      const value = Number(result?.settlement?.voucherValue ?? result?.voucher?.value ?? 0);
      toast.success(
        code
          ? `Buchung storniert. Kulanz-Gutschein ${code} über ${value.toFixed(2)} € ausgestellt.`
          : 'Buchung storniert und Kulanz-Gutschein ausgestellt.'
      );
      setGoodwillCancellationOpen(false);
      onUpdate(booking._id, { firstName: 'STORNIERT: ', status: 'canceled' });
      onClose();
    } catch (error) {
      console.error('Error during goodwill cancellation', error);
      toast.error('Fehler beim Kulanz-Storno.');
    }
    setGoodwillCanceling(false);
  };

  // Revoke a spontaneous reduction: deactivates the issued MOP-show voucher(s), the customer
  // is informed by mail and the reduction no longer counts — so it can be issued again
  // (e.g. split into per-person vouchers instead of one big voucher).
  const handleRevokeSpontaneousReduction = async () => {
    const reduction = revokeReductionTarget;
    if (!booking || !reduction?._id) return;
    setRevokingReduction(true);
    try {
      const result = await revokeSpontaneousReduction({
        bookingId: booking._id,
        reductionId: reduction._id,
      });
      let refreshed = null;
      try {
        refreshed = await getInternalBookingById(booking._id);
      } catch (_) {
        refreshed = null;
      }
      const nextBooking = refreshed || result?.booking || null;
      if (nextBooking) {
        setBooking(nextBooking);
        onUpdate(booking._id, nextBooking);
      }
      const codes = (result?.revokedVouchers || []).map(v => v.voucherCode);
      toast.success(
        codes.length > 0
          ? `Gutschein${codes.length > 1 ? 'e' : ''} ${codes.join(', ')} widerrufen. Der Kunde wurde per E-Mail informiert.`
          : 'Vorgemerkte Reduktion widerrufen.'
      );
      setRevokeReductionTarget(null);
    } catch (error) {
      console.error('Error revoking spontaneous reduction', error);
      const message =
        error?.data?.code === 'MOP_VOUCHER_ALREADY_REDEEMED' || error?.code === 'MOP_VOUCHER_ALREADY_REDEEMED'
          ? 'Gutschein wurde bereits (teilweise) eingelöst und kann nicht widerrufen werden.'
          : 'Fehler beim Widerruf des MOP-Show-Gutscheins.';
      toast.error(message);
    }
    setRevokingReduction(false);
  };

  // Admin override of the spontaneous-reduction gate: perform the plain (refunding) reduction.
  const handleForceReduce = async () => {
    const ctx = reductionGateDialog;
    setReductionGateDialog(null);
    if (!ctx || !booking) return;
    try {
      const result = await cancelBookingParticipants({
        bookingId: booking._id,
        categoryId: ctx.categoryId,
        countToCancel: ctx.countToCancel,
        forceReduce: true,
      });
      if (result?.booking) {
        setBooking(result.booking);
        onUpdate(booking._id, result.booking);
      }
      toast.success('Teilnehmer reduziert.');
    } catch (err) {
      toast.error('Fehler beim Reduzieren der Teilnehmer.');
      console.error('Error force-reducing participants', err);
    }
  };

  const handleOpenConvertToOfflineConfirm = async () => {
    const unpaidPaymentIds = openPaymentSummary.unpaidRelatedOnlinePaymentIds;

    if (!unpaidPaymentIds || unpaidPaymentIds.length === 0) {
      toast.error('Keine offene Online-Zahlung gefunden');
      return;
    }

    setPotentialDuplicateBookings([]);
    setDuplicateBookingCheckFailed(false);
    setConvertToOfflineConfirmOpen(true);
    if (!booking?._id) {
      return;
    }

    setLoadingDuplicateBookings(true);
    try {
      const duplicateBookings = await getPotentialDuplicateInternalBookings(booking._id);
      setPotentialDuplicateBookings(Array.isArray(duplicateBookings) ? duplicateBookings : []);
    } catch (error) {
      setDuplicateBookingCheckFailed(true);
      console.error('Error checking duplicate bookings:', error);
    }
    setLoadingDuplicateBookings(false);
  };

  const handleCloseConvertToOfflineConfirm = () => {
    if (convertingPayment) {
      return;
    }
    setConvertToOfflineConfirmOpen(false);
  };

  const handleMergeVoucherBookings = async () => {
    if (!booking?._id || voucherMergeCandidates.length === 0) return;
    setMerging(true);
    try {
      const allIds = [booking._id, ...voucherMergeCandidates.map(c => c._id)];
      const result = await mergeVoucherBookings(allIds);
      if (result?.success && result?.booking?._id) {
        toast.success('Buchungen erfolgreich zusammengeführt');
        setMergeDialogOpen(false);
        onUpdate(result.booking._id, result.booking);
        onClose();
      } else {
        toast.error('Fehler beim Zusammenführen der Buchungen');
      }
    } catch (_) {
      toast.error('Fehler beim Zusammenführen der Buchungen');
    }
    setMerging(false);
  };

  const handleConvertToOffline = async () => {
    setConvertingPayment(true);
    try {
      const unpaidPaymentIds = openPaymentSummary.unpaidRelatedOnlinePaymentIds;

      if (!unpaidPaymentIds || unpaidPaymentIds.length === 0) {
        toast.error('Keine offene Online-Zahlung gefunden');
        setConvertingPayment(false);
        return;
      }

      await Promise.allSettled(unpaidPaymentIds.map(id => convertToOfflinePaymentSilent(id)));
      // Refresh the booking data
      const updatedBooking = await getInternalBookingById(effectiveBookingId);
      setBooking(updatedBooking);
      onUpdate(effectiveBookingId, updatedBooking);
      updateEditableFromBooking(updatedBooking, hasUnlimitedEditRights);
      setConvertToOfflineConfirmOpen(false);
    } catch (error) {
      toast.error('Fehler beim Konvertieren der Zahlung');
      console.error('Error converting payments:', error);
    }
    setConvertingPayment(false);
  };

  // Triggered by the "Buchung stornieren" button. Within the early grace window we first show a
  // confirmation warning (the online payment might still complete); otherwise cancel directly.
  const handleRequestCancelPayments = () => {
    const unpaidPaymentIds = openPaymentSummary.unpaidRelatedOnlinePaymentIds;

    if (!unpaidPaymentIds || unpaidPaymentIds.length === 0) {
      toast.error('Keine offene Online-Zahlung gefunden');
      return;
    }

    if (isPaymentWithinGraceWindow) {
      setCancelPaymentConfirmOpen(true);
      return;
    }

    handleCancelPayments();
  };

  const handleCancelPayments = async () => {
    setConvertingPayment(true);
    try {
      const unpaidPaymentIds = openPaymentSummary.unpaidRelatedOnlinePaymentIds;

      if (!unpaidPaymentIds || unpaidPaymentIds.length === 0) {
        toast.error('Keine offene Online-Zahlung gefunden');
        setConvertingPayment(false);
        return;
      }

      await Promise.allSettled(unpaidPaymentIds.map(id => cancelPaymentsSilent(id)));
      // Refresh the booking data
      const updatedBooking = await getInternalBookingById(effectiveBookingId);
      setBooking(updatedBooking);
      onUpdate(effectiveBookingId, updatedBooking);
      updateEditableFromBooking(updatedBooking, hasUnlimitedEditRights);
      setCancelPaymentConfirmOpen(false);
    } catch (error) {
      toast.error('Fehler beim Stornieren der Zahlung');
      console.error('Error canceling payments:', error);
    }
    setConvertingPayment(false);
  };

  const handleRemoveUpsell = async item => {
    const onSuccess = result => {
      if (result && result.booking) {
        setBooking(result.booking);
        onUpdate(booking._id, result.booking);
      }
    };
    try {
      const result = await updateUpsells(
        {
          bookingId: booking._id,
          upsellId: item.referenceId,
          count: 0,
        },
        'Deine Anfrage für das Entfernen von ' +
          booking.upsells.find(upsell => upsell.upgradeId === item.referenceId).name +
          ' zur Buchung von ' +
          booking.firstName +
          ' ' +
          booking.lastName +
          ' wurde gespeichert.',
        null,
        {
          Typ: 'Upsell entfernen',
          Kunde: `${booking.firstName} ${booking.lastName}`,
          Upsell: booking.upsells.find(upsell => upsell.upgradeId === item.referenceId).name,
        },
        onSuccess,
        'Upsell erfolgreich entfernt.'
      );
      if (result) {
        onSuccess(result);
      }
    } catch (error) {
      toast.error('Fehler beim Entfernen des Upsells');
      console.error('Error removing upsell', error);
    }
  };

  const handleResourceUpdate = (bookingId, updatedBooking) => {
    // Update local state in the modal
    setBooking(updatedBooking);
    // Update parent component (grid view)
    onUpdate(bookingId, updatedBooking);
  };

  const handleNoShowUpdated = (updatedBookingId, updatedBooking) => {
    if (!updatedBooking) return;
    setBooking(updatedBooking);
    onUpdate?.(updatedBookingId, updatedBooking);
  };

  const handleNoShowInvoiceSent = async () => {
    if (!booking?._id) return;
    const updatedBooking = await getInternalBookingById(booking._id);
    if (!updatedBooking) return;
    setBooking(updatedBooking);
    onUpdate?.(booking._id, updatedBooking);
  };

  const startEditingSpecialLineItem = async item => {
    setEditingSpecialLineItem(item._id);
    setSpecialLineItemCount(item.count);
  };

  const stopEditingSpecialLineItem = async () => {
    try {
      // Find the current lineItem to get its _id
      const currentLineItem = booking.lineItems.find(item => item._id === editingSpecialLineItem);

      if (!currentLineItem) {
        toast.error('Kein lineItem mit der angegebenen ID gefunden');
        console.error('Lineitem not found');
        return;
      }

      if (specialLineItemCount !== currentLineItem.count) {
        const result = await updateSpecialLineItems({
          bookingId: booking._id,
          lineItemId: currentLineItem._id,
          count: specialLineItemCount,
        });

        if (result && result.booking) {
          setBooking(result.booking);
          onUpdate(booking._id, result.booking);
        }
      }

      setEditingSpecialLineItem(null);
      setSpecialLineItemCount(null);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Positionsanzahl');
      console.error('Error updating special line item count', error);
    }
  };

  const handleRemoveSpecialLineItem = async item => {
    try {
      const result = await updateSpecialLineItems({
        bookingId: booking._id,
        lineItemId: item._id,
        count: 0,
      });
      if (result && result.booking) {
        setBooking(result.booking);
        onUpdate(booking._id, result.booking);
      }
    } catch (error) {
      toast.error('Fehler beim Entfernen der Position');
      console.error('Error removing special line item', error);
    }
  };

  const handleConfirmPending = async () => {
    const toastMessage = `Die Bestätigungsanfrage für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann nach Identifizierung abgeschlossen werden`;
    const successToastMessage = 'Buchung erfolgreich bestätigt';
    const displayInfo = {
      Typ: 'Buchungsanfrage bestätigen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Buchungsnummer: booking.bookingNumber,
      Termin: dayjs(booking.startTime).format('DD.MM.YYYY HH:mm'),
    };
    const onSuccessCallback = updatedBooking => {
      if (updatedBooking) {
        setBooking(updatedBooking);
        onUpdate(booking._id, updatedBooking);
        toast.success(successToastMessage);
      }
    };
    try {
      await confirmPendingBooking(
        booking._id,
        toastMessage,
        null,
        displayInfo,
        onSuccessCallback,
        successToastMessage
      );
    } catch (error) {
      if (error.message !== 'INSUFFICIENT_RIGHTS') {
        console.error('Error confirming pending booking:', error);
      }
    }
  };

  const handleRemoveVoucher = async item => {
    try {
      const voucherCode =
        String(item?.resourceVoucherCode || '').trim().toUpperCase() ||
        String(item?.description || '')
          .match(/RV-([A-Z0-9]+)/i)?.[1]
          ?.toUpperCase() ||
        '';
      if (!voucherCode) {
        toast.error('Der Gutscheincode konnte nicht ermittelt werden.');
        return;
      }
      const toastMessage = 'Deine Anfrage für das Entfernen eines Gutscheins wurde gespeichert.';
      const successToastMessage = 'Gutschein erfolgreich entfernt.';
      const onSuccess = result => {
        if (result && result.booking) {
          setBooking(result.booking);
          onUpdate(booking._id, result.booking);
        }
      };
      const res = await removeVoucherFromBooking(
        { bookingId: booking._id, voucherIdOrCode: voucherCode },
        toastMessage,
        null,
        { Typ: 'Gutschein entfernen', Kunde: `${booking.firstName} ${booking.lastName}` },
        onSuccess,
        successToastMessage
      );
      if (res) onSuccess(res);
    } catch (error) {
      toast.error('Fehler beim Entfernen des Gutscheins');
      console.error('Error removing voucher:', error);
    }
  };

  const shell = (
    <Box
      data-testid='booking-details-modal'
      sx={{
        ...(embedded
          ? {
              position: 'relative',
              top: 'auto',
              left: 'auto',
              transform: 'none',
              width: '100%',
              maxWidth: 'none',
              height: '100%',
              maxHeight: '100%',
              bgcolor: 'background.paper',
              boxShadow: 'none',
              p: 2,
              overflow: 'auto',
              borderRadius: 0,
              outline: 'none',
              '&:focus': { outline: 'none' },
            }
          : {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: '1200px',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              overflow: 'auto',
              borderRadius: '10px',
              outline: 'none',
              '&:focus': { outline: 'none' },
            }),
        ...(computedBorderColor && { border: `2px solid ${computedBorderColor}` }),
        ...(containerSx && typeof containerSx === 'object' ? containerSx : null),
      }}
    >
      {loading ? (
        <Stack spacing={2}>
          <Skeleton variant='text' width='60%' height={40} />
          <Skeleton variant='rectangular' width='100%' height={100} />
          <Stack direction='row' spacing={2}>
            <Skeleton variant='rectangular' width='30%' height={200} />
            <Skeleton variant='rectangular' width='70%' height={200} />
          </Stack>
          <Skeleton variant='rectangular' width='100%' height={300} />
        </Stack>
      ) : !booking ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <BookingHeader
            booking={booking}
            onClose={handleClose}
            onPresentClick={handlePresentClick}
            onRewindCheckinClick={handleOpenRewindCheckinConfirm}
            onRewindCheckoutClick={handleOpenRewindCheckoutConfirm}
            onShiftClick={handleShiftClick}
            onCancelClick={handleCancelClick}
            onSpontaneousReductionClick={handleOpenSpontaneousReduction}
            canSpontaneousReduce={canSpontaneousReduce}
            onGoodwillCancelClick={() => setGoodwillCancellationOpen(true)}
            canGoodwillCancel={canGoodwillCancel}
            onCheckoutClick={handleCheckoutClick}
            onUpdateFirstName={handleUpdateFirstName}
            onUpdateLastName={handleUpdateLastName}
            presentCountdown={presentCountdown}
            cancelCountdown={cancelCountdown}
            checkoutCountdown={checkoutCountdown}
            lockCancelBtn={lockCancelBtn}
            lockCheckoutBtn={lockCheckoutBtn}
            isEditable={isEditableEffective}
            isWorkflowEditable={isWorkflowEditableEffective}
            hasUpdateRights={effectiveHasUpdateRightsUi}
            hasMinorUpdateRights={effectiveHasMinorUpdateRights}
            hasFullUpdateRights={effectiveHasFullUpdateRightsUi}
            hasUnlimitedEditRights={effectiveHasUnlimitedEditRightsUi}
            hasRewindCheckinRights={effectiveHasRewindCheckinRights}
            isTodayorFutue={isTodayorFutue}
            isToday={isToday}
            isWithinNext70Hours={isWithinNext70Hours}
            hasPastCancelRights={effectiveHasPastCancelRightsUi}
            hasLastMinuteCancelRights={effectiveHasLastMinuteCancelRightsUi}
            currentlyEditingField={currentlyEditingField}
            onFieldEditStart={handleFieldEditStart}
            onFieldEditEnd={handleFieldEditEnd}
            onConfirmClick={handleConfirmPending}
            hasConfirmRights={effectiveHasConfirmRightsUi}
            isRegiondoOnlinePaidBooking={isRegiondoOnlinePaidBooking}
            canCancelRegiondoOnlinePaidBooking={canCancelRegiondoOnlinePaidBooking}
            hasChangeGroupTypeRights={hasChangeGroupTypeRights}
            onGroupTypeBadgeClick={handleGroupTypeBadgeClick}
            changingGroupType={changingGroupType}
          />
          <Dialog
            open={rewindCheckinConfirmOpen}
            onClose={handleCloseRewindCheckinConfirm}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitle>Check-in zurücksetzen?</DialogTitle>
            <DialogContent>
              <Typography variant='body2' sx={{ mt: 1 }}>
                Möchtest du den Check-in für {booking?.firstName} {booking?.lastName} wirklich
                zurücksetzen?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseRewindCheckinConfirm} disabled={rewindCheckinLoading}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='warning'
                onClick={handleConfirmRewindCheckin}
                disabled={rewindCheckinLoading}
              >
                {rewindCheckinLoading ? <CircularProgress size={18} /> : 'Zurücksetzen'}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={rewindCheckoutConfirmOpen}
            onClose={handleCloseRewindCheckoutConfirm}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitle>Checkout zurücksetzen?</DialogTitle>
            <DialogContent>
              <Typography variant='body2' sx={{ mt: 1 }}>
                Möchtest du den Checkout für {booking?.firstName} {booking?.lastName} wirklich
                zurücksetzen?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseRewindCheckoutConfirm} disabled={rewindCheckoutLoading}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='warning'
                onClick={handleConfirmRewindCheckout}
                disabled={rewindCheckoutLoading}
              >
                {rewindCheckoutLoading ? <CircularProgress size={18} /> : 'Zurücksetzen'}
              </Button>
            </DialogActions>
          </Dialog>
          {booking.status === 'confirmed' &&
            booking.productModel === 'ResourceVoucherProduct' &&
            voucherMergeCandidates.length > 0 && (
              <Alert
                severity='warning'
                sx={{ mb: 2 }}
                action={
                  <Button
                    color='inherit'
                    size='small'
                    variant='outlined'
                    onClick={() => setMergeDialogOpen(true)}
                  >
                    Zusammenführen
                  </Button>
                }
              >
                <AlertTitle>Getrennte Gutschein-Buchungen erkannt</AlertTitle>
                {`${voucherMergeCandidates.length + 1} Buchungen mit gleicher E-Mail und gleichem Zeitslot gefunden. Wurden Gutscheine einzeln eingelöst statt gemeinsam? Buchungen zusammenführen empfohlen.`}
              </Alert>
            )}
          {booking.status === 'awaitingConfirmation' && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              <AlertTitle>Buchung wartet auf Bestätigung</AlertTitle>
              <Typography variant='body2'>
                Diese Buchung wurde noch nicht bestätigt und wartet auf eine Bestätigung durch einen
                Mitarbeiter.
              </Typography>
            </Alert>
          )}
          {isRegiondoOnlinePaidBooking ? (
            <Alert
              severity='info'
              sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}
              action={
                effectiveHasRegiondoOnlinePaidEditRights ? (
                  <Button size='small' variant='outlined' onClick={handleOpenRegiondo}>
                    In Regiondo öffnen
                  </Button>
                ) : null
              }
            >
              <AlertTitle>Regiondo-Buchung</AlertTitle>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                {effectiveHasRegiondoOnlinePaidEditRights
                  ? 'Änderungen an dieser Buchung sind nur durch eine vollständige Stornierung in FunZone und Regiondo möglich. (Bei PayPal-Zahlungen muss zusätzlich das Rückerstattungsformular ausgefüllt werden.)'
                  : 'Änderungen und Stornierungen an dieser Buchung müssen durch das Callcenter oder die Standortleitung durchgeführt werden.'}
              </Typography>
            </Alert>
          ) : isRegiondoOfflinePaidBooking ? (
            <Alert
              severity='info'
              variant='outlined'
              sx={{ mb: 2, py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}
              action={
                regiondoBookingKey ? (
                  <Button size='small' variant='text' onClick={handleOpenRegiondo}>
                    In Regiondo öffnen
                  </Button>
                ) : null
              }
            >
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                Diese Buchung wurde aus Regiondo importiert.
              </Typography>
            </Alert>
          ) : null}
          {hasUnpaidOnlinePayments && (
            <WaitingForPaymentBanner
              onSendPaymentLink={
                hasSendPaymentLinkRights && booking?.mail
                  ? () => setSendPaymentLinkOpen(true)
                  : undefined
              }
              sendingPaymentLink={sendingPaymentLink}
              onConvertToOffline={handleOpenConvertToOfflineConfirm}
              onCancelPayments={handleRequestCancelPayments}
              convertingPayment={convertingPayment}
              hasUpdateRights={effectiveHasUpdateRightsUi}
              isActionable={hasUnpaidOnlinePayments}
            />
          )}
          {booking.status === 'blocked' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant='body1'>
                <b>Zeitraum:</b>{' '}
                {`${dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')} - ${dayjs(
                  booking.endTime
                ).format('DD.MM.YYYY HH:mm')}`}
              </Typography>
            </Box>
          )}
          {booking.status !== 'blocked' ? (
            <>
            <BookingGroupSection
              bookingGroup={booking.bookingGroup}
              currentBookingId={booking._id}
              onOpenSibling={siblingId =>
                setSiblingView({ base: bookingId, id: siblingId })
              }
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  md:
                    canRenderNoShowActionsRow ||
                    canShowUnpaidBookingInvoiceRow ||
                    ['KaraokeZone', 'ExitZone'].includes(booking.entertainment)
                      ? 'minmax(0, 1fr) minmax(220px, 280px)'
                      : 'minmax(0, 1fr)',
                },
                gap: 2,
                alignItems: { xs: 'stretch', md: 'start' },
              }}
            >
              <Box sx={{ minWidth: 0, order: { xs: 1, md: 0 } }}>
                <BookingInfoSection
                  booking={booking}
                  prepTimeMinutes={prepTimeMinutes}
                  prepTimeWarnings={prepTimeWarnings}
                  isDev={isDev}
                  isToday={isToday}
                  isEditable={isEditableEffective}
                  hasFullUpdateRights={effectiveHasFullUpdateRightsUi}
                  hasEmailUpdateRights={effectiveHasCustomerEmailUpdateRightsUi}
                  hasUpdateRights={effectiveHasUpdateRightsUi}
                  onUpdateCompanyName={handleUpdateCompanyName}
                  onUpdateTableNumbers={handleUpdateTableNumbers}
                  lockPaymentBtn={lockPaymentBtn}
                  currentlyEditingField={currentlyEditingField}
                  onFieldEditStart={handleFieldEditStart}
                  onFieldEditEnd={handleFieldEditEnd}
                  onUpdateMail={handleUpdateMail}
                  onUpdatePhone={handleUpdatePhone}
                  onResendConfirmation={openResendConfirm}
                  isResendingConfirmation={resendingConfirmation}
                  onSetPaymentModalOpen={setPaymentModalOpen}
                  canMarkBookingAsPaid={
                    isToday &&
                    booking?.status !== 'noShow' &&
                    openPaymentSummary.hasUnpaidOfflinePayments &&
                    !openPaymentSummary.hasUnpaidOnlinePayments
                  }
                  canSendPaymentLink={canSendPaymentLinkOffline}
                  sendingPaymentLink={sendingPaymentLink}
                  onSendPaymentLink={() => setSendPaymentLinkOpen(true)}
                  onInitiateCall={handleInitiateCall}
                  canShowCallButton={canShowCallButton}
                  hasActiveCallAgent={hasActiveCallAgent}
                  hasOverrideCapacityRights={hasOverrideCapacityRights}
                  onOpenCapacityOverrideDialog={openCapacityOverrideDialog}
                  onRevokeSpontaneousReduction={
                    !isPreview ? reduction => setRevokeReductionTarget(reduction) : undefined
                  }
                  revokingSpontaneousReduction={revokingReduction}
                />
              </Box>

              {((['KaraokeZone', 'ExitZone'].includes(booking.entertainment)) || canRenderNoShowActionsRow || canShowUnpaidBookingInvoiceRow) && (
                  <Box
                    sx={{
                      order: { xs: 3, md: 0 },
                      maxWidth: { xs: '100%', md: 280 },
                      width: { xs: '100%', md: 'auto' },
                      justifySelf: { xs: 'stretch', md: 'end' },
                      pr: { xs: 0, md: 6 },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    {booking.entertainment &&
                      ['KaraokeZone', 'ExitZone'].includes(booking.entertainment) && (
                        <ResourceSection
                          booking={booking}
                          hasUpdateRights={effectiveHasUpdateRightsUi}
                          onUpdate={handleResourceUpdate}
                          borderColor={computedBorderColor}
                          onClose={handleClose}
                        />
                      )}
                    {canRenderNoShowActionsRow && (
                      <Stack spacing={1} alignItems='stretch' sx={{ minWidth: 220 }}>
                        {hasMarkNoShowRight && (
                          <MarkNoShowButton
                            booking={booking}
                            onBookingUpdated={handleNoShowUpdated}
                            variant='outlined'
                            size='medium'
                            fullWidth
                          />
                        )}
                        <RevertNoShowButton
                          booking={booking}
                          onBookingUpdated={handleNoShowUpdated}
                          variant='outlined'
                          size='medium'
                          fullWidth
                        />
                        {hasSendNoShowInvoiceRight && hasNoShowInvoiceAdyenMerchant(booking) && (
                          <>
                            <NoShowInvoiceButton
                              booking={booking}
                              onAfterSend={handleNoShowInvoiceSent}
                              variant='outlined'
                              size='medium'
                              fullWidth
                            />
                            <SkipNoShowInvoiceButton
                              booking={booking}
                              onBookingUpdated={handleNoShowUpdated}
                              onAfterSkip={handleNoShowInvoiceSent}
                              variant='outlined'
                              size='medium'
                              fullWidth
                            />
                          </>
                        )}
                      </Stack>
                    )}
                    {canShowUnpaidBookingInvoiceRow && (
                      <Stack spacing={1} alignItems='stretch' sx={{ minWidth: 220 }}>
                        <UnpaidBookingInvoiceButton
                          booking={booking}
                          onAfterSend={handleNoShowInvoiceSent}
                          variant='outlined'
                          size='medium'
                          fullWidth
                        />
                        <SkipUnpaidBookingInvoiceButton
                          booking={booking}
                          onBookingUpdated={handleNoShowUpdated}
                          onAfterSkip={handleNoShowInvoiceSent}
                          variant='outlined'
                          size='medium'
                          fullWidth
                        />
                      </Stack>
                    )}
                  </Box>
                )}
              <Box sx={{ gridColumn: '1 / -1', minWidth: 0, order: { xs: 2, md: 0 } }}>
                <OrderedResourceVisitPlan
                  booking={bookingForOrderedResourcesDisplay}
                  open={open}
                />
              </Box>
            </Box>
          {customerComment && (
            <Alert severity='info' sx={{ mb: 2 }}>
              <AlertTitle>Kundenhinweis</AlertTitle>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                {customerComment}
              </Typography>
              {booking?.customerCommentAt && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ mt: 0.5 }}
                >
                  Eingegangen am {dayjs(booking.customerCommentAt).format('DD.MM.YYYY HH:mm')} Uhr
                </Typography>
              )}
            </Alert>
          )}
          {booking.entertainment === 'LaserZone' &&
            (isTodayorFutue || !!booking?.finishedAt) && (
              <MemberCardStatusSection
                memberCardStats={memberCardStats}
                onManageCards={() => setMemberCardModalOpen(true)}
                onShowQr={() => {
                  if (!booking?.presentSince) {
                    toast.info(
                      'Der Onsite-Check-in ist erst möglich, wenn die Buchung eingecheckt wurde.'
                    );
                    return;
                  }

                  setOnsiteQrOpen(true);
                }}
              />
            )}
          {(isTodayorFutue || !!booking?.finishedAt) && (
            <WaiverStatusSection bookingId={booking?._id} />
          )}
          </>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant='body1'>
                <b>Buchungsnummer:</b>{' '}
                {(() => {
                  const v = booking?.bookingNumber;
                  if (v == null) return '—';
                  if (typeof v === 'object') {
                    try {
                      if (Object.keys(v).length === 0) return '—';
                    } catch (_) {}
                  }
                  return String(v);
                })()}
              </Typography>
            </Box>
          )}

          {booking.lineItems && booking.lineItems.length > 0 && (
            <BookingLineItemsTable
              booking={booking}
              editingLineItem={editingLineItem}
              lineItemCount={lineItemCount}
              oldLineItemCount={oldLineItemCount}
              availableCapacity={availableCapacity}
              minParticipants={minParticipants}
              maxParticipants={maxParticipants}
              canOverbook={hasOverbookRights}
              overbookEnabled={overbookEnabled}
              onOverbookToggle={setOverbookEnabled}
              editingUpsell={editingUpsell}
              upsellCount={upsellCount}
              upsellType={upsellType}
              editingSpecialLineItem={editingSpecialLineItem}
              specialLineItemCount={specialLineItemCount}
              isTodayorFutue={isTodayorFutue}
              hasUpdateRights={effectiveHasUpdateRightsUi}
              isEditable={isEditableEffective}
              hasUnlimitedEditRights={
                effectiveHasUnlimitedEditRightsUi && !hasPendingOnlinePaymentLock
              }
              hasPastCancelRights={effectiveHasPastCancelRightsUi}
              canReduceParticipants={!plainReductionBlocked}
              onStartEditingLineItem={startEditingLineItem}
              onStopEditingLineItem={stopEditingLineItem}
              onSetLineItemCount={setLineItemCount}
              onStartEditingUpsell={startEditingUpsell}
              onStopEditingUpsell={stopEditingUpsell}
              onSetUpsellCount={setUpsellCount}
              onStartEditingSpecialLineItem={startEditingSpecialLineItem}
              onStopEditingSpecialLineItem={stopEditingSpecialLineItem}
              onSetSpecialLineItemCount={setSpecialLineItemCount}
              onRemovePackage={handleRemovePackage}
              onRemoveUpsell={handleRemoveUpsell}
              onRemoveSpecialLineItem={handleRemoveSpecialLineItem}
              onSetSelectedLineItem={setSelectedLineItem}
              onSetLineItemPaymentModalOpen={setLineItemPaymentModalOpen}
              onRemoveVoucher={handleRemoveVoucher}
              hasOverridePricesRight={hasOverridePricesRight}
              onOverrideLineItemPrice={handleOverrideLineItemPrice}
              addSpecialItemOpen={!isPreview && hasOverridePricesRight && addSpecialItemOpen}
              onAddSpecialItem={async (desc, count, price) => {
                await handleAddSpecialLineItem(desc, count, price);
                setAddSpecialItemOpen(false);
              }}
              onCancelAddSpecialItem={() => setAddSpecialItemOpen(false)}
            />
          )}

          {!isPreview ? (
            <ActionButtons
              booking={booking}
              isTodayorFutue={isTodayorFutue}
              hasUpdateRights={effectiveHasUpdateRightsUi}
              isEditable={isEditableEffective}
              hasUnlimitedEditRights={
                effectiveHasUnlimitedEditRightsUi && !hasPendingOnlinePaymentLock
              }
              onAddPackageClick={handleAddPackageClick}
              onAddUpsellClick={handleAddUpsellClick}
              hasAddCategoryRights={hasAddCategoryRights}
              allCategoriesUsed={allCategoriesUsed}
              onAddCategoryClick={handleAddCategoryClick}
              hasSpecialItemRights={hasOverridePricesRight && !addSpecialItemOpen}
              onAddSpecialItemClick={() => setAddSpecialItemOpen(true)}
            />
          ) : null}

          <BookingCommentSection
            bookingId={booking._id != null ? String(booking._id) : undefined}
            comment={comment}
            onCommentChange={setComment}
            hasMinorUpdateRights={
              effectiveHasMinorUpdateRights ||
              (isRegiondoOnlinePaidBooking && effectiveHasRegiondoOnlinePaidEditRights)
            }
            saving={saving}
          />

          {((hasSuppressEmailsRight && booking.status !== 'blocked') || (canSeeAddress && (hasAddress || hasBirthdate))) && (
            <Box sx={{ mt: 1 }}>
              <Accordion
                disableGutters
                sx={{
                  mt: 1,
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <AccordionSummary
                  expandIcon={canSeeAddress && (hasAddress || hasBirthdate) ? <ExpandMoreIcon /> : null}
                  sx={{
                    minHeight: 32,
                    '& .MuiAccordionSummary-content': {
                      my: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent:
                        canSeeAddress && (hasAddress || hasBirthdate) ? 'space-between' : 'flex-start',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      width: '100%',
                      mr: 1,
                    },
                    ...(!canSeeAddress || (!hasAddress && !hasBirthdate) ? { cursor: 'default !important', '&:hover': { cursor: 'default' } } : {}),
                  }}
                  {...(!canSeeAddress || (!hasAddress && !hasBirthdate) ? { onClick: e => e.preventDefault() } : {})}
                >
                  {canSeeAddress && (hasAddress || hasBirthdate) ? (
                    <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                      Weitere Kontaktdaten
                    </Typography>
                  ) : null}
                  {hasSuppressEmailsRight && booking.status !== 'blocked' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(booking.suppressCustomerEmails)}
                          onChange={async e => {
                            try {
                              await handleUpdateField('suppressCustomerEmails', e.target.checked);
                            } catch (_) {}
                          }}
                          color='error'
                          size='small'
                        />
                      }
                      label={
                        <Typography variant='body2' color={booking.suppressCustomerEmails ? 'error' : 'text.secondary'} sx={{ whiteSpace: 'nowrap' }}>
                          E-Mails unterdrücken
                        </Typography>
                      }
                      sx={{
                        mr: 0,
                        ml: canSeeAddress && (hasAddress || hasBirthdate) ? 'auto' : 0,
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  )}
                </AccordionSummary>
                {canSeeAddress && (hasAddress || hasBirthdate) && (
                  <AccordionDetails sx={{ pt: 0 }}>
                    {hasAddress && (
                      <Box>
                        {addressLines.map((line, idx) => (
                          <React.Fragment key={`address-line-${idx}-${line}`}>
                            <Typography variant='caption' color='text.secondary'>
                              {idx === 0 ? <b>Adresse: </b> : ''}
                              {line}
                            </Typography>
                            <br />
                          </React.Fragment>
                        ))}
                      </Box>
                    )}
                    {hasBirthdate && (
                      <Typography variant='caption' color='text.secondary'>
                        <b>Geburtsdatum: </b> {birthdateDisplay}
                      </Typography>
                    )}
                  </AccordionDetails>
                )}
              </Accordion>
            </Box>
          )}

          {!isPreview ? (
            <FooterButtons
              booking={booking}
              hasOverrideCapacityRights={hasOverrideCapacityRights}
              isEditable={isEditableEffective}
              onOpenCapacityOverrideDialog={openCapacityOverrideDialog}
              onCommunicationHistoryOpen={() => setCommunicationHistoryOpen(true)}
              onInvoiceModalOpen={() => setInvoiceModalOpen(true)}
              onOnSiteProtocolModalOpen={() => setOnSiteProtocolModalOpen(true)}
              onPaymentHistoryModalOpen={() => setPaymentHistoryModalOpen(true)}
              onBookingHistoryModalOpen={() => setBookingHistoryModalOpen(true)}
              onPosTransferOpen={
                hasPosTransferRight &&
                openPaymentSummary.hasUnpaidOfflinePayments &&
                (booking?.entertainmentId?.copagoCounters || []).length > 0
                  ? () => setPosTransferOpen(true)
                  : undefined
              }
              externalInvoice={externalInvoice}
              onExternalInvoiceCreate={
                booking?.entertainmentId?.isExternallyManaged && authCtx.checkRightAny('invoice:create:external')
                  ? () => setExternalInvoiceDialogOpen(true)
                  : undefined
              }
              onExternalInvoiceView={async () => {
                if (!externalInvoice?._id) return;
                try {
                  const blob = await getInvoicePDFById(externalInvoice._id);
                  if (blob) {
                    if (extPdfUrl) window.URL.revokeObjectURL(extPdfUrl);
                    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                    setExtPdfUrl(url);
                    setExtPdfViewerOpen(true);
                  }
                } catch (e) {
                  toast.error('Fehler beim Anzeigen der Rechnung.');
                }
              }}
              onExternalInvoiceDownload={async () => {
                if (!externalInvoice?._id) return;
                try {
                  const blob = await getInvoicePDFById(externalInvoice._id);
                  if (blob) {
                    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Rechnung_${externalInvoice.invoiceNumber || 'extern'}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  }
                } catch (e) {
                  toast.error('Fehler beim Herunterladen der Rechnung.');
                }
              }}
            />
          ) : null}
        </>
      )}
    </Box>
  );

  return (
    <>
      {embedded ? (
        shell
      ) : (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby='booking-details-modal'
          aria-describedby='booking-details-description'
          sx={{
            '& .MuiBackdrop-root': {
              outline: 'none',
            },
            '& .MuiModal-root': {
              outline: 'none',
            },
          }}
        >
          {shell}
        </Modal>
      )}
      {!isPreview && booking && (
        <>
          <PackageSelectionDialog
            open={packageSelectionOpen}
            onClose={() => setPackageSelectionOpen(false)}
            availablePackages={availablePackages}
            onPackageSelect={handlePackageSelect}
            participantCount={booking?.participantCount || 0}
          />
          <ShiftBookingDialog
            key={booking._id}
            open={shiftDialogOpen}
            onClose={() => setShiftDialogOpen(false)}
            booking={booking}
            bookingStartTime={booking?.startTime}
            currentMonth={currentMonth}
            currentYear={currentYear}
            availableDates={availableDates}
            availableTimes={availableTimes}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            loadingDates={loadingDates}
            loadingTimes={loadingTimes}
            onNavigate={handleDateNavigation}
            onDateSelect={handleDateSelect}
            onTimeSelect={handleTimeSelect}
            onShiftConfirm={handleShiftConfirm}
            canOverbook={hasOverbookRights}
            overbookEnabled={shiftOverbookEnabled}
            onOverbookToggle={handleShiftOverbookToggle}
          />
          <CommunicationHistoryModal
            open={communicationHistoryOpen}
            onClose={() => setCommunicationHistoryOpen(false)}
            booking={booking}
          />
          <InvoiceModal
            open={invoiceModalOpen}
            onClose={() => setInvoiceModalOpen(false)}
            bookingId={booking._id}
          />
          <ExternalInvoiceDialog
            open={externalInvoiceDialogOpen}
            onClose={() => setExternalInvoiceDialogOpen(false)}
            onCreated={async invoice => {
              setExternalInvoiceDialogOpen(false);
              setExternalInvoice(invoice);
              try {
                const blob = await getInvoicePDFById(invoice._id);
                if (blob) {
                  if (extPdfUrl) window.URL.revokeObjectURL(extPdfUrl);
                  const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                  setExtPdfUrl(url);
                  setExtPdfViewerOpen(true);
                }
              } catch (e) {
                toast.error('Fehler beim Anzeigen der Rechnung.');
              }
            }}
            createExternalInvoice={createExternalInvoice}
            booking={booking}
            onBookingNotPaid={(payload) => {
              setPendingExternalInvoicePayload(payload);
              setExternalInvoiceDialogOpen(false);
              setExtPaymentWarningOpen(true);
            }}
            onMarkBookingPaidAfterInvoice={() => {
              setOpenExtInvoiceAfterPayment(true);
              setPaymentModalOpen(true);
            }}
          />

          <Dialog
            open={extPaymentWarningOpen}
            onClose={() => setExtPaymentWarningOpen(false)}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitle>Buchung noch nicht bezahlt</DialogTitle>
            <DialogContent>
              <Typography variant='body2'>
                Die Buchung ist noch nicht als bezahlt markiert. Um eine „Bezahlt"-Rechnung auszustellen,
                muss die Buchung zuerst als bezahlt markiert werden.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExtPaymentWarningOpen(false)}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                onClick={() => {
                  setExtPaymentWarningOpen(false);
                  setOpenExtInvoiceAfterPayment(true);
                  setPaymentModalOpen(true);
                }}
              >
                Jetzt als bezahlt markieren
              </Button>
            </DialogActions>
          </Dialog>
          <LateArrivalDialog
            open={lateArrivalModalOpen}
            onClose={() => setLateArrivalModalOpen(false)}
            onSelectReason={handleLateArrivalSelect}
          />
          <PaymentDialog
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            booking={booking}
            paymentCountdown={paymentCountdown}
            lockPaymentBtn={lockPaymentBtn}
            allowMarkAsPaid={
              openPaymentSummary.hasUnpaidOfflinePayments &&
              !openPaymentSummary.hasUnpaidOnlinePayments
            }
            onPaymentClick={handlePaymentClick}
            onMarkLineItemPaid={handleMarkLineItemPaid}
          />
          <PosTransferDialog
            open={posTransferOpen}
            onClose={() => setPosTransferOpen(false)}
            booking={booking}
          />
          <OnSiteProtocolModal
            open={onSiteProtocolModalOpen}
            onClose={() => setOnSiteProtocolModalOpen(false)}
            booking={booking}
          />
          <BookingHistoryModal
            open={bookingHistoryModalOpen}
            onClose={() => setBookingHistoryModalOpen(false)}
            booking={booking}
          />
          <PaymentHistoryModal
            open={paymentHistoryModalOpen}
            onClose={() => setPaymentHistoryModalOpen(false)}
            booking={booking}
          />
          <CheckoutDialog
            open={checkoutDialogOpen}
            onClose={() => {
              setCheckoutDialogOpen(false);
              setCheckoutCountdown(0);
            }}
            selectedRating={selectedRating}
            onSetSelectedRating={setSelectedRating}
            checkoutLoading={checkoutLoading}
            lockCheckoutBtn={lockCheckoutBtn}
            onCheckout={handleCheckout}
          />
          <BadRatingConfirmDialog
            open={badRatingConfirmOpen}
            onClose={() => setBadRatingConfirmOpen(false)}
            onConfirm={runCheckout}
            loading={checkoutLoading}
            customerName={`${booking?.firstName || ''} ${booking?.lastName || ''}`.trim()}
          />
          <UpsellSelectionDialog
            open={upsellSelectionOpen}
            onClose={() => setUpsellSelectionOpen(false)}
            availableUpsells={availableUpsells}
            booking={booking}
            onUpsellSelect={handleUpsellSelect}
            participantCount={booking?.participantCount || 0}
          />
          <Dialog
            open={categoryDialogOpen}
            onClose={() => !addingCategory && setCategoryDialogOpen(false)}
          >
            <DialogTitle>Personenkategorie hinzufügen</DialogTitle>
            <DialogContent>
              {(() => {
                const as = categoryDialogAgeSplit;
                const mapping = as?.ageSplitMapping;
                const currentGt = as?.currentGroupType;
                const currentGtLabel = currentGt === 'kids' ? 'Kinder' : 'Erwachsene';
                const otherGtLabel = currentGt === 'kids' ? 'Erwachsene' : 'Kinder';
                const currentPoolFree = as?.currentPoolFree ?? null;
                const otherPoolFree = as?.otherPoolFree ?? null;
                const participantCount = booking?.participantCount || 0;

                const maxAddCurrent = currentPoolFree != null
                  ? Math.max(0, currentPoolFree - participantCount) : null;
                const maxAddOther = otherPoolFree != null
                  ? Math.max(0, otherPoolFree - participantCount) : null;
                const hardMax = mapping
                  ? Math.max(maxAddCurrent ?? 0, maxAddOther ?? 0)
                  : null;

                const deriveFlip = (catName, count) => {
                  if (!mapping || !catName) return null;
                  const kidsNames = new Set(mapping.kids || []);
                  const adultsNames = new Set(mapping.adults || []);
                  const existing = (booking?.lineItems || [])
                    .filter(li => li.type === 'booking' && li.status === 'active')
                    .map(li => ({ name: li.description?.split(' - ')[0], count: li.count }));
                  const preview = [...existing, { name: catName, count }];
                  let kc = 0, ac = 0;
                  for (const c of preview) {
                    if (kidsNames.has(c.name)) kc += c.count;
                    else if (adultsNames.has(c.name)) ac += c.count;
                  }
                  if (kc === 0 && ac === 0) return null;
                  return kc >= ac ? 'kids' : 'adults';
                };

                const derivedGt = deriveFlip(selectedCategoryName, selectedCategoryCount);
                const wouldFlip = derivedGt && derivedGt !== currentGt;
                const totalAfter = participantCount + selectedCategoryCount;

                let hint = null;
                if (mapping && selectedCategoryName && selectedCategoryCount >= 1) {
                  if (wouldFlip) {
                    const flipPoolFree = currentGt === 'kids'
                      ? (derivedGt === 'adults' ? otherPoolFree : currentPoolFree)
                      : (derivedGt === 'kids' ? otherPoolFree : currentPoolFree);
                    if (flipPoolFree != null && flipPoolFree < totalAfter) {
                      hint = (
                        <Typography variant='body2' sx={{ color: 'warning.main' }}>
                          Gruppentyp würde zu „{derivedGt === 'kids' ? 'Kinder' : 'Erwachsene'}" wechseln,
                          aber Kapazität reicht nicht ({totalAfter} benötigt, {flipPoolFree} frei).
                          Gruppentyp „{currentGtLabel}" wird beibehalten.
                          Verfügbar: {maxAddCurrent ?? '?'} weitere Plätze
                        </Typography>
                      );
                    } else {
                      hint = (
                        <Typography variant='body2' sx={{ color: 'info.main' }}>
                          Gruppentyp wechselt zu „{derivedGt === 'kids' ? 'Kinder' : 'Erwachsene'}".
                          Verfügbar: {flipPoolFree != null ? flipPoolFree - totalAfter : '?'} weitere Plätze
                        </Typography>
                      );
                    }
                  } else if (maxAddCurrent != null && selectedCategoryCount > maxAddCurrent) {
                    hint = (
                      <Typography variant='body2' sx={{ color: 'error.main' }}>
                        Nicht genügend Kapazität im {currentGtLabel}-Pool
                        ({totalAfter} benötigt, {currentPoolFree} frei)
                      </Typography>
                    );
                  } else if (maxAddCurrent != null) {
                    hint = (
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        {currentGtLabel}-Pool: {maxAddCurrent} weitere Plätze verfügbar
                      </Typography>
                    );
                  }
                }

                return (
                  <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
                    <FormControl fullWidth>
                      <InputLabel id='category-select-label'>Kategorie</InputLabel>
                      <Select
                        labelId='category-select-label'
                        value={selectedCategoryName}
                        label='Kategorie'
                        onChange={e => {
                          setSelectedCategoryName(e.target.value);
                          setSelectedCategoryCount(1);
                        }}
                      >
                        {categoryDialogCategories.map(cat => (
                          <MenuItem key={cat.name} value={cat.name}>
                            {cat.name}
                            {cat.price != null &&
                              ` — ${Number(cat.price).toFixed(2).replace('.', ',')} €`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <NumberField
                      label='Anzahl'
                      value={selectedCategoryCount}
                      min={1}
                      max={
                        hardMax != null && !categoryDialogOverbookEnabled
                          ? Math.max(1, hardMax)
                          : undefined
                      }
                      onValueChange={setSelectedCategoryCount}
                      fullWidth
                    />
                    {hasOverbookRights && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={categoryDialogOverbookEnabled}
                            onChange={e => setCategoryDialogOverbookEnabled(e.target.checked)}
                            color='warning'
                          />
                        }
                        label={
                          <Typography variant='body2' sx={{ color: 'warning.main' }}>
                            Überbuchen erlauben (Kapazitätsgrenzen werden ignoriert)
                          </Typography>
                        }
                      />
                    )}
                    {categoryDialogOverbookEnabled ? (
                      <Typography variant='body2' sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                        Achtung: Überbuchung aktiv – Kapazität wird überschritten!
                      </Typography>
                    ) : (
                      hint
                    )}
                  </Stack>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCategoryDialogOpen(false)} disabled={addingCategory}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                onClick={handleAddCategoryConfirm}
                disabled={addingCategory || !selectedCategoryName || selectedCategoryCount < 1}
              >
                {addingCategory ? <CircularProgress size={20} /> : 'Hinzufügen'}
              </Button>
            </DialogActions>
          </Dialog>
          <LineItemPaymentModal
            open={lineItemPaymentModalOpen}
            onClose={() => {
              setLineItemPaymentModalOpen(false);
              setSelectedLineItem(null);
            }}
            lineItem={selectedLineItem}
          />
          <MemberCardQueueModal
            open={memberCardModalOpen}
            onClose={() => setMemberCardModalOpen(false)}
            booking={booking}
            readOnly={!!booking?.finishedAt}
            borderColor={computedBorderColor}
            onBookingUpdated={updated => {
              if (!updated) return;
              setBooking(updated);
              onUpdate(booking._id, updated);
            }}
          />
          <OnsiteQrModal
            open={onsiteQrOpen}
            onClose={() => setOnsiteQrOpen(false)}
            booking={booking}
            onBookingUpdated={updated => {
              if (!updated) return;
              setBooking(updated);
              onUpdate(booking._id, updated);
            }}
          />
          <ConvertToOfflineDialog
            open={convertToOfflineConfirmOpen}
            onClose={handleCloseConvertToOfflineConfirm}
            onConfirm={handleConvertToOffline}
            converting={convertingPayment}
            loadingDuplicates={loadingDuplicateBookings}
            duplicateCheckFailed={duplicateBookingCheckFailed}
            duplicateBookings={potentialDuplicateBookings}
            withinGraceWindow={isPaymentWithinGraceWindow}
          />
          <Dialog
            open={cancelPaymentConfirmOpen}
            onClose={() => {
              if (!convertingPayment) setCancelPaymentConfirmOpen(false);
            }}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle>Buchung trotz laufender Online-Zahlung stornieren?</DialogTitle>
            <DialogContent dividers>
              <Alert severity='warning'>
                Die Online-Zahlung wurde gerade erst gestartet und könnte vom Kunden noch
                abgeschlossen werden. Normalerweise warten wir die ersten Minuten ab. Bitte nur
                stornieren, wenn der Kunde vor Ort ist bzw. sicher ist, dass er nicht mehr zahlt.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCancelPaymentConfirmOpen(false)}
                disabled={convertingPayment}
              >
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='warning'
                onClick={handleCancelPayments}
                disabled={convertingPayment}
              >
                {convertingPayment ? (
                  <CircularProgress size={18} color='inherit' />
                ) : (
                  'Trotzdem stornieren'
                )}
              </Button>
            </DialogActions>
          </Dialog>
          <GoodwillCancellationDialog
            open={goodwillCancellationOpen}
            onClose={() => {
              if (!goodwillCanceling) setGoodwillCancellationOpen(false);
            }}
            onConfirm={handleConfirmGoodwillCancellation}
            onPreview={fetchGoodwillCancellationPreview}
            submitting={goodwillCanceling}
          />

          <SpontaneousReductionDialog
            open={spontaneousReductionOpen}
            onClose={handleCloseSpontaneousReduction}
            onConfirm={handleConfirmSpontaneousReduction}
            onPreview={fetchSpontaneousReductionPreview}
            submitting={spontaneousReducing}
            booking={booking}
            // With ticket insurance only reductions that undershoot the booked upgrade minimum may
            // use the voucher flow; everything above it must stay a refunding cancellation.
            minimumRemainingParticipants={
              insuranceAllowsReductionByUpgradeMinimum ? bookedUpgradeMinimumCount : 0
            }
            initialCount={spontaneousReductionInitialCount}
          />
          <Dialog
            open={!!reductionGateDialog}
            onClose={() => setReductionGateDialog(null)}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle>Teilnehmer reduzieren</DialogTitle>
            <DialogContent dividers>
              {reductionGateDialog?.reason === 'upgradeMinimum' ? (
                <Typography variant='body2'>
                  <strong>
                    {reductionGateDialog?.upgradeName || 'Ein gebuchtes Paket/Upgrade'}
                  </strong>{' '}
                  setzt mindestens{' '}
                  <strong>{reductionGateDialog?.minimumCount} Teilnehmer</strong> voraus — eine
                  Stornierung mit Erstattung ist darunter nicht möglich (auch nicht mit
                  Sonderrechten). Du kannst die freigewordenen Plätze stattdessen als{' '}
                  <strong>MOP-Show-Gutschein</strong> umwandeln (voller Preis bleibt bestehen).
                </Typography>
              ) : (
                <Typography variant='body2'>
                  Innerhalb von 7 Tagen ohne Ticketversicherung: Du kannst die freigewordenen Plätze
                  als <strong>MOP-Show-Gutschein</strong> umwandeln (voller Preis bleibt bestehen)
                  {reductionGateDialog?.canForceReduce ? (
                    <>
                      {' '}– oder die Teilnehmer <strong>trotzdem frei reduzieren</strong> (mit
                      Erstattung).
                    </>
                  ) : (
                    '.'
                  )}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Button sx={{ mr: 'auto' }} onClick={() => setReductionGateDialog(null)}>
                Abbrechen
              </Button>
              {reductionGateDialog?.canForceReduce && (
                <Button variant='outlined' color='error' onClick={handleForceReduce}>
                  Trotzdem frei reduzieren
                </Button>
              )}
              <Button
                variant='contained'
                color='warning'
                onClick={() => {
                  // Carry the count staff already entered over into the voucher dialog.
                  setSpontaneousReductionInitialCount(reductionGateDialog?.countToCancel || null);
                  setReductionGateDialog(null);
                  setSpontaneousReductionOpen(true);
                }}
              >
                <LockIconWrapper right='internalBooking:special:spontaneousReduction'>
                  In Gutschein umwandeln
                </LockIconWrapper>
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={!!revokeReductionTarget}
            onClose={() => !revokingReduction && setRevokeReductionTarget(null)}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle>MOP-Show-Gutschein widerrufen?</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                {revokeReductionTarget?.status === 'pendingPayment' ? (
                  <Typography variant='body2'>
                    Die vorgemerkte Reduktion wird widerrufen. Es wurde noch kein Gutschein
                    ausgestellt.
                  </Typography>
                ) : (
                  <>
                    <Typography variant='body2'>
                      Der/die ausgestellte(n) MOP-Show-Gutschein(e) über insgesamt{' '}
                      <strong>
                        {Number(revokeReductionTarget?.voucherValue || 0).toFixed(2)} €
                      </strong>{' '}
                      werden <strong>deaktiviert</strong> und können nicht mehr eingelöst werden.
                    </Typography>
                    <Typography variant='body2'>
                      Der Kunde wird per <strong>E-Mail</strong> über die Deaktivierung informiert.
                    </Typography>
                  </>
                )}
                <Typography variant='body2'>
                  Die {revokeReductionTarget?.count} reduzierte
                  {revokeReductionTarget?.count === 1 ? '' : 'n'} Person
                  {revokeReductionTarget?.count === 1 ? '' : 'en'} zählen danach wieder als
                  anwesend — die Reduktion kann anschließend erneut durchgeführt werden (z. B.
                  aufgeteilt in einen Gutschein pro Person).
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setRevokeReductionTarget(null)}
                disabled={revokingReduction}
              >
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='error'
                onClick={handleRevokeSpontaneousReduction}
                disabled={revokingReduction}
              >
                {revokingReduction ? (
                  <CircularProgress size={18} color='inherit' />
                ) : (
                  <LockIconWrapper right='internalBooking:special:spontaneousReduction'>
                    Widerrufen
                  </LockIconWrapper>
                )}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={mergeDialogOpen}
            onClose={() => !merging && setMergeDialogOpen(false)}
            maxWidth='sm'
            fullWidth
          >
            <DialogTitle>Buchungen zusammenführen?</DialogTitle>
            <DialogContent>
              <Typography variant='body2' sx={{ mt: 1 }}>
                {`Es werden ${voucherMergeCandidates.length + 1} Buchungen zu einer einzigen Buchung zusammengeführt:`}
              </Typography>
              <Box component='ul' sx={{ mt: 1, mb: 1, pl: 2 }}>
                <Typography component='li' variant='body2'>
                  {`${booking?.bookingNumber} – ${booking?.firstName} ${booking?.lastName} (${booking?.participantCount || 0} Person${(booking?.participantCount || 0) === 1 ? '' : 'en'})`}
                </Typography>
                {voucherMergeCandidates.map(c => (
                  <Typography key={c._id} component='li' variant='body2'>
                    {`${c.bookingNumber} – ${c.firstName} ${c.lastName} (${c.participantCount || 0} Person${(c.participantCount || 0) === 1 ? '' : 'en'})`}
                  </Typography>
                ))}
              </Box>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Die einzelnen Buchungen werden storniert, die Gutscheine bleiben eingelöst. Der Kunde erhält eine neue Buchungsbestätigung.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setMergeDialogOpen(false)} disabled={merging}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='warning'
                onClick={handleMergeVoucherBookings}
                disabled={merging}
              >
                {merging ? <CircularProgress size={18} /> : 'Jetzt zusammenführen'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={resendConfirmOpen} onClose={closeResendConfirm} maxWidth='xs' fullWidth>
            <DialogTitle>Bestätigung erneut senden?</DialogTitle>
            <DialogContent>
              <Typography variant='body2' sx={{ mt: 1 }}>
                Möchtest du die Buchungsbestätigung an {booking?.mail || 'diese E-Mail'} erneut
                senden?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={closeResendConfirm} disabled={resendingConfirmation}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleResendConfirmation}
                disabled={resendingConfirmation || !booking?.mail}
              >
                {resendingConfirmation ? <CircularProgress size={18} /> : 'Senden'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={sendPaymentLinkOpen}
            onClose={() => !sendingPaymentLink && setSendPaymentLinkOpen(false)}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitle>Bezahllink senden?</DialogTitle>
            <DialogContent>
              <Typography variant='body2' sx={{ mt: 1 }}>
                {sendPaymentLinkMode === 'switch'
                  ? `Die offene Vor-Ort-Zahlung wird auf Online-Zahlung umgestellt (die Servicepauschale für die Vor-Ort-Zahlung entfällt) und ${
                      booking?.mail || 'der Kunde'
                    } erhält den Bezahllink per E-Mail. Zahlt der Kunde nicht, fällt die Buchung 24 Stunden vor dem Termin automatisch auf Zahlung vor Ort zurück – die Buchung bleibt in jedem Fall bestehen.`
                  : `${
                      booking?.mail || 'Der Kunde'
                    } erhält den Bezahllink für die offene Online-Zahlung erneut per E-Mail. Bezahlt werden kann bis 24 Stunden vor dem Termin; danach wird die Buchung automatisch auf Zahlung vor Ort umgestellt.`}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setSendPaymentLinkOpen(false)} disabled={sendingPaymentLink}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleSendPaymentLink}
                disabled={sendingPaymentLink || !booking?.mail}
              >
                {sendingPaymentLink ? <CircularProgress size={18} /> : 'Senden'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={groupTypeConfirmOpen}
            onClose={() => !changingGroupType && setGroupTypeConfirmOpen(false)}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitle>Gruppentyp ändern?</DialogTitle>
            <DialogContent>
              {(() => {
                const currentGt = booking?.ltSpecific?.groupType;
                const currentLabel = currentGt === 'kids' ? 'Kinder' : 'Erwachsene';
                const targetLabel = currentGt === 'kids' ? 'Erwachsene' : 'Kinder';
                const participantCount = booking?.participantCount || 0;
                const otherPoolFree = groupTypeCapacityInfo?.otherPoolFree;
                const fits = otherPoolFree != null && otherPoolFree >= participantCount;
                const notEnough = otherPoolFree != null && otherPoolFree < participantCount;

                return (
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <Typography variant='body2'>
                      {`Gruppentyp von „${currentLabel}" auf „${targetLabel}" ändern?`}
                    </Typography>
                    {loadingGroupTypeCapacity ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                          Kapazität wird geprüft…
                        </Typography>
                      </Box>
                    ) : notEnough ? (
                      <Typography variant='body2' sx={{ color: 'error.main' }}>
                        {`Nicht genügend Kapazität im ${targetLabel}-Pool (${participantCount} benötigt, ${otherPoolFree} frei).`}
                      </Typography>
                    ) : fits ? (
                      <Typography variant='body2' sx={{ color: 'success.main' }}>
                        {`${targetLabel}-Pool: ${otherPoolFree - participantCount} weitere Plätze verfügbar nach Wechsel`}
                      </Typography>
                    ) : null}
                  </Stack>
                );
              })()}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setGroupTypeConfirmOpen(false)}
                disabled={changingGroupType}
              >
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='warning'
                onClick={handleChangeGroupType}
                disabled={
                  changingGroupType ||
                  loadingGroupTypeCapacity ||
                  (groupTypeCapacityInfo?.otherPoolFree != null &&
                    groupTypeCapacityInfo.otherPoolFree < (booking?.participantCount || 0))
                }
              >
                {changingGroupType ? <CircularProgress size={18} /> : 'Ändern'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={!!groupTypeFlipDialog}
            onClose={() => setGroupTypeFlipDialog(null)}
            maxWidth='xs'
            fullWidth
          >
            <DialogTitle>Kapazitätskonflikt durch Gruppentyp-Wechsel</DialogTitle>
            <DialogContent>
              <Typography variant='body2' sx={{ mt: 1 }}>
                {groupTypeFlipDialog?.message}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setGroupTypeFlipDialog(null)}>
                Abbrechen
              </Button>
              <Button
                variant='contained'
                color='warning'
                onClick={async () => {
                  const fn = groupTypeFlipDialog?.retryFn;
                  setGroupTypeFlipDialog(null);
                  if (fn) await fn();
                }}
              >
                Ja, beibehalten
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={capacityOverrideDialogOpen}
            onClose={() => setCapacityOverrideDialogOpen(false)}
          >
            <DialogTitle>Kapazitätsübersteuerung</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1, minWidth: 280 }}>
                <TextField
                  label='Kapazität Teilnehmer (Override)'
                  type='number'
                  value={capacityOverrideEditParticipant}
                  onChange={e => setCapacityOverrideEditParticipant(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  placeholder='Leer = Standard'
                  size='small'
                />
                <TextField
                  label='Kapazität Bahnen (Override)'
                  type='number'
                  value={capacityOverrideEditLane}
                  onChange={e => setCapacityOverrideEditLane(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  placeholder='Leer = Standard'
                  size='small'
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCapacityOverrideDialogOpen(false)}>Abbrechen</Button>
              <Button
                variant='contained'
                onClick={saveCapacityOverride}
                disabled={capacityOverrideSaving}
              >
                {capacityOverrideSaving ? <CircularProgress size={20} /> : 'Speichern'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={extPdfViewerOpen}
            onClose={() => {
              setExtPdfViewerOpen(false);
              if (extPdfUrl) window.URL.revokeObjectURL(extPdfUrl);
              setExtPdfUrl(null);
            }}
            maxWidth='lg'
            fullWidth
            PaperProps={{ sx: { height: '85vh' } }}
          >
            <AppBar position='static' color='default' elevation={1}>
              <Toolbar>
                <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
                  {externalInvoice && `Rechnung ${externalInvoice.invoiceNumber}`}
                </Typography>
                <Tooltip title='Rechnung herunterladen'>
                  <IconButton
                    color='primary'
                    onClick={async () => {
                      if (!externalInvoice?._id) return;
                      try {
                        const blob = await getInvoicePDFById(externalInvoice._id);
                        if (blob) {
                          const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `Rechnung_${externalInvoice.invoiceNumber || 'extern'}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        }
                      } catch (e) {
                        toast.error('Fehler beim Herunterladen der Rechnung.');
                      }
                    }}
                    sx={{ mr: 2 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <IconButton
                  edge='end'
                  color='inherit'
                  onClick={() => {
                    setExtPdfViewerOpen(false);
                    if (extPdfUrl) window.URL.revokeObjectURL(extPdfUrl);
                    setExtPdfUrl(null);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Box sx={{ p: 0, height: 'calc(85vh - 64px)' }}>
              {extPdfUrl && (
                <iframe
                  src={`${extPdfUrl}#toolbar=0&navpanes=0`}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title='PDF Viewer'
                />
              )}
            </Box>
          </Dialog>

        </>
      )}
    </>
  );
}

export default BookingDetailsModal;
